import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Checkbox } from '@/components/ui/checkbox';
import { useMediaQuery } from '@/lib/hooks';
import { useTranslation } from 'react-i18next';
import imageGenerationService from '../../../services/imageGenerationService';
import bulkPhotoService from '../../../services/bulkPhotoService';
import questionPaperService from '../../../services/questionPaperService';
import { SHOP_CARD_MM, buildShopCard } from './shopCard';
import { sanitizeText } from '../../../utils/sanitize';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import { notifications } from '@/lib/notifications';
import SelectField from '@/components/common/SelectField';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import { toBnDigits } from '../../../utils/bnFormat';
import { isSpecialBgToken, specialBgCss, paintSpecialBg } from './bgPresets';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem } from '@/components/ui/dropdown-menu';
import { jsPDF } from 'jspdf';
import {
    IconPrinter,
    IconX,
    IconLayoutGrid,
    IconRectangleVertical,
    IconRectangle,
    IconRotateClockwise,
    IconScissors,
    IconBorderOuter,
    IconMinus,
    IconPlus,
    IconArrowsMaximize,
    IconFileTypePdf,
    IconFileTypeJpg,
    IconFileTypePng,
    IconPhoto,
    IconTrash,
    IconChevronDown,
    IconCheck,
    IconSearch,
    IconAlignCenter,
    IconAlignRight,
    IconSparkles,
    IconAdjustments,
    IconCamera,
    IconTemplate,
    IconLoader2,
    IconStack2,
    IconId,
} from '@tabler/icons-react';
import {
    DndContext,
    MouseSensor,
    TouchSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import { useDraggable } from '@dnd-kit/core';
import { PHOTO_PRINT_SIZES, photoPrintDims, photoPrintSizeOf } from '@/constants/photoPrintSizes';
import styles from './PrintModal.module.scss';

// Custom-styled tooltip wrapper — replaces the native `title` attribute (which
// renders the unstyled browser tooltip) on the modal's action buttons.
const Tip = ({ label, children }) => (
    <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent className="z-[10002]">{label}</TooltipContent>
    </Tooltip>
);

// Output page/paper sizes. `w`/`h` are the PORTRAIT millimetre dimensions (w ≤ h);
// landscape swaps them. Selecting one resizes the actual sheet (and the exported PDF),
// so images lay out directly on that paper — useful for printing straight onto photo
// paper (e.g. a 4R / 4×6 sheet). 'custom' reads the width/height inputs instead.
const PAGE_SIZES = [
    { key: 'a4',     label: 'A4 — 210×297mm',        w: 210, h: 297 },
    { key: 'a5',     label: 'A5 — 148×210mm',        w: 148, h: 210 },
    { key: 'letter', label: 'Letter — 216×279mm',    w: 216, h: 279 },
    { key: '2r',     label: '2R — 64×89mm',          w: 64,  h: 89  },
    { key: '3r',     label: '3R — 89×127mm',         w: 89,  h: 127 },
    { key: '4r',     label: '4R / 4×6 — 102×152mm',  w: 102, h: 152 },
    { key: '5r',     label: '5R — 127×178mm',        w: 127, h: 178 },
    { key: '6r',     label: '6R — 152×203mm',        w: 152, h: 203 },
    { key: 'custom', label: 'Custom…',               w: 100, h: 150 },
];

// QR item bounding box on the sheet (mm): a square code plus a caption row below.
const QR_ITEM = { w: 38, h: 46 };


// Today's date in Bengali (e.g. "২ আগস্ট, ২০২৬").
function getBnToday() {
    return new Date().toLocaleDateString('bn-BD', { year: 'numeric', month: 'long', day: 'numeric' });
}

// Read a Blob into a data-URL (canvas-safe, same-origin) for PDF rasterisation.
function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
    });
}

// Load a (data-)URL into an <img> element for canvas drawing.
function loadImageEl(src) {
    return new Promise((resolve, reject) => {
        const im = new Image();
        im.onload = () => resolve(im);
        im.onerror = reject;
        im.src = src;
    });
}

// object-fit:cover draw. `posTop` puts the vertical crop at the top (matches the
// on-screen `object-position: center top`); otherwise it centres.
function drawImageCoverPos(ctx, img, dx, dy, dw, dh, posTop) {
    const iw = img.naturalWidth || img.width;
    const ih = img.naturalHeight || img.height;
    if (!iw || !ih || dw <= 0 || dh <= 0) return;
    const ir = iw / ih;
    const dr = dw / dh;
    let sx, sy, sw, sh;
    if (ir > dr) { sh = ih; sw = ih * dr; sx = (iw - sw) / 2; sy = 0; }
    else { sw = iw; sh = iw / dr; sx = 0; sy = posTop ? 0 : (ih - sh) / 2; }
    ctx.drawImage(img, sx, sy, sw, sh, dx, dy, dw, dh);
}

// Named background ids → hex (for transparent cut-outs whose stored `background` is a
// legacy id rather than a hex). Transparent AI masters store a hex or 'transparent'.
const PRINT_BG_HEX = { white: '#ffffff', blue: '#2196F3', sky_blue: '#03A9F4', grey: '#9E9E9E', dark_blue: '#3068E1', teal: '#00897B', off_white: '#FFF8DC' };
// Resolve a stored bg token to a solid colour, or null (transparent / special token).
const resolveSolidBg = (token) => {
    if (!token || token === 'transparent') return null;
    return PRINT_BG_HEX[token] || (String(token).startsWith('#') || String(token).startsWith('rgb') ? token : null);
};

// Draggable Image Item Component
function DraggableImageItem({ id, image, onRemove, onRotate, onToggleSelect, selected, showCuttingBorder, showImageBorder, imageGap = 0, resizable = false, onResize, onResizeStart, getMmPerPixel, bgStyle = {} }) {
    const { t } = useTranslation();
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id });

    // Corner-resize — any photo while ফ্রি সাইজ is on, and the serial card always (its shape kept, so the drawing never
    // stretches): pointerdown on a handle stops the dnd-kit drag, then window-level pointermove streams new mm dims to the parent.
    const [hovered, setHovered] = useState(false);
    const startResize = (corner) => (e) => {
        if (!(resizable || image.sizeType === 'card') || e.button !== 0) return;
        e.stopPropagation();
        e.preventDefault();
        onResizeStart?.(id);
        const startX = e.clientX, startY = e.clientY;
        const start = { w: image.width, h: image.height, x: image.x, y: image.y };
        const mmPerPx = getMmPerPixel?.() || 0.265;
        const move = (ev) => {
            const dx = (ev.clientX - startX) * mmPerPx;
            const dy = (ev.clientY - startY) * mmPerPx;
            let { w, h, x, y } = { w: start.w, h: start.h, x: start.x, y: start.y };
            if (image.sizeType === 'card') {
                // keep the card's shape: the width follows the pointer, the height follows the width, the opposite corner stays put
                const ratio = start.h / start.w;
                w = Math.max(18, Math.min(140, start.w + (corner.includes('e') ? dx : -dx)));
                h = w * ratio;
                if (corner.includes('w')) x = start.x + start.w - w;
                if (corner.includes('n')) y = start.y + start.h - h;
                onResize?.(id, { width: w, height: h, x, y });
                return;
            }
            if (corner.includes('e')) w = start.w + dx;
            if (corner.includes('s')) h = start.h + dy;
            if (corner.includes('w')) { w = start.w - dx; x = start.x + dx; }
            if (corner.includes('n')) { h = start.h - dy; y = start.y + dy; }
            onResize?.(id, { width: w, height: h, x, y });
        };
        const up = () => {
            window.removeEventListener('pointermove', move);
            window.removeEventListener('pointerup', up);
        };
        window.addEventListener('pointermove', move);
        window.addEventListener('pointerup', up);
    };

    const rot = image.rotation || 0;
    const isTransposed = rot === 90 || rot === 270;
    // QR/barcode items render their own content (code image + shop-name caption) instead
    // of the photo cover-frame, and skip rotation / image-border.
    const isQr = image.sizeType === 'qr';
    // The shop card (shopCard.js) is one finished image: it fills its box, with no photo frame, border or resize.
    const isCard = image.sizeType === 'card';

    // Deterministic QR geometry (mm). Mirrors the canvas/PDF math in handleDownloadPdf:
    // 2mm padding each side, 6mm reserved for the caption row, QR drawn as a square inside
    // the remaining area. Positioning the <img> and caption with absolute mm coords (instead
    // of letting flex resolve heights) keeps the code undistorted AND the caption unclipped
    // in the browser print rasteriser, which mangled both with the flex layout.
    const QR_PAD_MM = 2;
    const qrCapMm = image.caption ? 6 : 0;
    const qrAreaMm = isQr ? Math.max(0, image.height - 2 * QR_PAD_MM - qrCapMm) : 0;
    const qrSquareMm = isQr ? Math.max(0, Math.min(image.width - 2 * QR_PAD_MM, qrAreaMm)) : 0;

    const renderedW = isTransposed ? image.height : image.width;
    const renderedH = isTransposed ? image.width  : image.height;

    // The slot size equals the DB-stored image dimensions. The visible photo lives in a
    // FRAME centred inside the slot:
    //   • Frame SIZE keeps the slot aspect ratio and comes from the gap (imageGap/4 per
    //     side for stamps, imageGap/2 for passports). So every stamp is the SAME size, and
    //     a bigger gap SHRINKS the photo (scales it) instead of re-cropping it.
    //   • Frame is CENTRED, so the gap between any two adjacent stamps is equal on all
    //     sides. (Equal gaps and a bigger margin on the page-edge side are mutually
    //     exclusive in a fixed slot, so we keep the gaps even.)
    const isStamp = image.sizeType === 'stamp';
    const uniformInset = isStamp ? imageGap / 4 : imageGap / 2;

    const cAvailW = Math.max(0, renderedW - 2 * uniformInset);
    const cAvailH = Math.max(0, renderedH - 2 * uniformInset);
    const slotAspect = renderedH > 0 ? renderedW / renderedH : 1;
    let frameW = cAvailW;
    let frameH = slotAspect > 0 ? cAvailW / slotAspect : cAvailH;
    if (frameH > cAvailH) { frameH = cAvailH; frameW = cAvailH * slotAspect; }

    const frameLeft = Math.max(0, renderedW - frameW) / 2;
    const frameTop  = Math.max(0, renderedH - frameH) / 2;

    const style = {
        width:  `${renderedW}mm`,
        height: `${renderedH}mm`,
        position: 'absolute',
        left: `${image.x}mm`,
        top: `${image.y}mm`,
        transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
        opacity: isDragging ? 0.5 : 1,
        cursor: isDragging ? 'grabbing' : 'grab',
        overflow: 'hidden',
        zIndex: isDragging ? 1000 : (image.sizeType === 'stamp' ? 2 : 1),
    };

    // Ctrl/Cmd-click toggles this image's membership in the multi-select. We listen
    // on pointerdown (before dnd-kit's drag-start) and stop propagation so the drag
    // sensor doesn't also pick it up.
    const handlePointerDown = (e) => {
        if ((e.ctrlKey || e.metaKey) && e.button === 0) {
            e.stopPropagation();
            e.preventDefault();
            onToggleSelect?.(id);
        }
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`${styles.imageItem}${selected ? ' ' + styles.selected : ''}`}
            {...attributes}
            {...listeners}
            onPointerDown={handlePointerDown}
            onPointerEnter={() => setHovered(true)}
            onPointerLeave={() => setHovered(false)}
            data-print-item="true"
            data-image-id={id}
        >
            {/* Cutting border — at slot boundary (gap is internal so the slot edge = cut line) */}
            {showCuttingBorder && (
                <div
                    className="cutting-border"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        border: '1px solid rgb(228 228 231)',
                        pointerEvents: 'none',
                        boxSizing: 'border-box',
                        zIndex: 5,
                    }}
                />
            )}
            {/* Corner handles — inset so the item's overflow:hidden can't clip them. Photos: while ফ্রি সাইজ is on; the serial card:
                on hover or when selected. */}
            {((resizable && !isQr && !isCard) || (isCard && (hovered || selected) && !isDragging)) && ['nw', 'ne', 'sw', 'se'].map((c) => (
                <span
                    key={c}
                    data-print-hide="true"
                    onPointerDown={startResize(c)}
                    style={{
                        position: 'absolute',
                        width: 11,
                        height: 11,
                        background: '#E68A00',
                        border: '1.5px solid #fff',
                        borderRadius: 3,
                        boxShadow: '0 0 3px rgba(0,0,0,0.35)',
                        zIndex: 20,
                        ...(c.includes('n') ? { top: 0 } : { bottom: 0 }),
                        ...(c.includes('w') ? { left: 0 } : { right: 0 }),
                        cursor: c === 'nw' || c === 'se' ? 'nwse-resize' : 'nesw-resize',
                        touchAction: 'none',
                    }}
                />
            ))}
            {/* QR item — code on top, active office-space name below. The content box is
                sized to the un-rotated dims and centred + rotated, so rotating swaps the
                slot footprint cleanly (matches how photos rotate). */}
            {isCard ? (
                <img
                    src={image.src}
                    alt="Shop card"
                    draggable={false}
                    // Like the QR: no transform unless rotated, so print doesn't resample it at screen resolution.
                    style={{
                        position: 'absolute',
                        ...(rot ? {
                            top: '50%',
                            left: '50%',
                            width: `${image.width}mm`,
                            height: `${image.height}mm`,
                            transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                        } : { top: 0, left: 0, width: '100%', height: '100%' }),
                        maxWidth: 'none',
                        pointerEvents: 'none',
                        userSelect: 'none',
                    }}
                />
            ) : isQr ? (
                // Absolutely-positioned QR + caption (NO flex). Flex main-axis heights
                // resolve differently inside the browser's print rasteriser, which both
                // distorted the code AND clipped the caption. Absolute coords in mm mirror
                // the canvas/PDF math exactly (QR square at top, caption row below), so the
                // preview, browser print, and PDF are pixel-for-pixel the same layout.
                <div style={{
                    position: 'absolute',
                    // NO transform when un-rotated. A CSS transform forces the browser to
                    // rasterise this element to an offscreen texture at SCREEN resolution and
                    // then scale it for print → the QR grid gets resampled and warped (the
                    // photos print crisp precisely because they use plain left/top, no
                    // transform). For the common rot===0 case we fill the item box directly
                    // with left/top; only the rare rotated case needs the centring transform.
                    ...(rot ? {
                        top: '50%',
                        left: '50%',
                        width: `${image.width}mm`,
                        height: `${image.height}mm`,
                        transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                    } : {
                        top: 0,
                        left: 0,
                        width: '100%',
                        height: '100%',
                    }),
                    boxSizing: 'border-box',
                    background: '#fff',
                    overflow: 'hidden',
                }}>
                    <img
                        src={image.src}
                        alt="QR code"
                        style={{
                            // Fixed square, centred horizontally via a plain left offset (NOT
                            // translateX, which would create its own scaled-in-print texture),
                            // pinned to the top pad. A w=h square PNG with uniform modules can't
                            // distort.
                            position: 'absolute',
                            top: `${QR_PAD_MM}mm`,
                            left: `${(image.width - qrSquareMm) / 2}mm`,
                            width: `${qrSquareMm}mm`,
                            height: `${qrSquareMm}mm`,
                            maxWidth: 'none',
                            display: 'block',
                            objectFit: 'contain',
                        }}
                    />
                    {image.caption ? (
                        <div style={{
                            position: 'absolute',
                            left: `${QR_PAD_MM}mm`,
                            right: `${QR_PAD_MM}mm`,
                            // Sits just below the reserved QR area (pad + qrArea height),
                            // matching the canvas caption baseline.
                            top: `${QR_PAD_MM + qrAreaMm + 1}mm`,
                            paddingTop: '1mm',
                            borderTop: '0.3mm solid #e4e4e7',
                            fontSize: '2.4mm',
                            lineHeight: 1.15,
                            fontWeight: 700,
                            textAlign: 'center',
                            color: '#18181b',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}>
                            {image.caption}
                        </div>
                    ) : null}
                </div>
            ) : (
            /* Photo frame — constant size (keeps slot aspect, sized from the uniform gap so
                every stamp is the same size), positioned per side so page-edge sides get the
                full gap. Uses left/top (not flex) so it survives the print `display:block` reset. */
            <div style={{
                position: 'absolute',
                left:   `${frameLeft}mm`,
                top:    `${frameTop}mm`,
                width:  `${frameW}mm`,
                height: `${frameH}mm`,
                overflow: 'hidden',
                // Composite the stored background colour behind a transparent cut-out.
                ...bgStyle,
            }}>
                <img
                    src={image.src}
                    alt="Print preview"
                    className={styles.imageContent}
                    // Rotated photos carry swapped mm dimensions inline; the print stylesheet
                    // must NOT force them to width/height:100% (that breaks the rotation crop).
                    data-rotated={isTransposed ? 'true' : undefined}
                    // For 90°/270° the pre-rotation <img> is sized to the frame's SWAPPED
                    // dimensions so that after rotate() its footprint fills the frame exactly.
                    style={isTransposed ? {
                        display: 'block',
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        width:  `${frameH}mm`, // → footprint height after rotate
                        height: `${frameW}mm`, // → footprint width  after rotate
                        // The swapped width can exceed the frame, so disable Tailwind's
                        // preflight `img { max-width:100% }` cap.
                        maxWidth: 'none',
                        maxHeight: 'none',
                        transform: `translate(-50%, -50%) rotate(${rot}deg)`,
                        objectFit: 'cover',
                        objectPosition: 'center',
                    } : {
                        display: 'block',
                        position: 'static',
                        width: '100%',
                        height: '100%',
                        transform: rot ? `rotate(${rot}deg)` : undefined,
                        objectFit: 'cover',
                        objectPosition: 'center top',
                    }}
                />
            </div>
            )}
            {/* Image border — hugs the photo INSIDE the gap (at the frame edge, unlike the
                cutting border which sits at the slot boundary outside the gap). */}
            {!isQr && !isCard && showImageBorder && (
                <div
                    className="image-border"
                    style={{
                        position: 'absolute',
                        left:   `${frameLeft}mm`,
                        top:    `${frameTop}mm`,
                        width:  `${frameW}mm`,
                        height: `${frameH}mm`,
                        border: '1px solid rgba(0, 0, 0, 0.6)',
                        boxSizing: 'border-box',
                        pointerEvents: 'none',
                        zIndex: 6,
                    }}
                />
            )}
            {/* Rotate button — top left (photos and QR items both rotate) */}
            <Tip label={t('imageGeneration.rotateImageTooltip') || 'Rotate 90°'}>
                <button
                    className={`${styles.rotateBtn} absolute top-0.5 left-0.5 w-5 h-5 rounded bg-orange-500 hover:bg-orange-600 text-white flex items-center justify-center z-10 rotate-btn-grid print:hidden`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRotate(id);
                    }}
                >
                    <IconRotateClockwise size={12} />
                </button>
            </Tip>
            {/* Remove button — top right */}
            <Tip label={t('imageGeneration.removeImageTooltip') || 'Remove'}>
                <button
                    className={`${styles.removeBtn} absolute top-0.5 right-0.5 w-5 h-5 rounded bg-red-500 hover:bg-red-600 text-white flex items-center justify-center z-10 remove-btn-grid print:hidden`}
                    onClick={(e) => {
                        e.stopPropagation();
                        onRemove(id);
                    }}
                >
                    <IconX size={12} />
                </button>
            </Tip>
        </div>
    );
}

// The sheet's download formats (the split button by Print). Images are the same 300-DPI sheet as Print and the PDF.
const DOWNLOAD_FORMATS = [
    { key: 'pdf',  label: 'PDF',  Icon: IconFileTypePdf },
    { key: 'jpg',  label: 'JPG',  Icon: IconFileTypeJpg, mime: 'image/jpeg', quality: 0.95 },
    { key: 'png',  label: 'PNG',  Icon: IconFileTypePng, mime: 'image/png' },
    { key: 'webp', label: 'WebP', Icon: IconPhoto,       mime: 'image/webp', quality: 0.95 },
];
const DL_FORMAT_KEY = 'sohozkaj_print_download_format';

// The image modules the slider can list (search box dropdown). `source` is the history feed's filter; the default feed holds the AI
// photos. Bulk Photo Edit has its own API: its finished photos, listed with a `bulk:` id prefix.
const PRINT_MODULES = [
    { key: 'ai',           source: null,           Icon: IconSparkles,   labelKey: 'printModule_ai' },
    { key: 'manual',       source: 'manual',       Icon: IconAdjustments, labelKey: 'printModule_manual' },
    { key: 'studio',       source: 'studio',       Icon: IconCamera,     labelKey: 'printModule_studio' },
    { key: 'ai-templates', source: 'ai-templates', Icon: IconTemplate,   labelKey: 'printModule_templates' },
    { key: 'bulk',         source: null,           Icon: IconStack2,     labelKey: 'printModule_bulk' },
];
const BULK_ID = /^bulk:(.+)$/;

// Bulk Photo Edit's finished photos, newest project first, as slider entries.
async function loadBulkPhotos(officeSpaceId) {
    const res = await bulkPhotoService.list(officeSpaceId ? { officeSpaceId } : {});
    return (res?.data || []).flatMap((p) => (p.images || [])
        .filter((i) => i.status === 'done' && i.enhancedUrl && !i.error)
        .map((i) => ({ id: `bulk:${i.id}`, src: i.enhancedUrl, name: i.name || p.name || '', createdAt: i.createdAt, background: null, photoSize: null })));
}

export default function PrintModal({ opened, onClose, imageUrl, imageId, isDualImage = false, imageHistory = [], initialSizeType = 'passport', initialSizeMm = null, historySource: historySourceProp = null }) {
    const { t, i18n } = useTranslation();
    const isEn = (i18n.language || '').startsWith('en');
    // Bengali-locale digit rendering for the size labels/hints (৪০×৫০, Ctrl + ১–৯…).
    const bnum = (v) => (isEn ? String(v) : toBnDigits(v));
    const logoLight = isEn ? '/images/logo-en.svg' : '/images/logo.svg';
    const logoDark = isEn ? '/images/logo-white-en.svg' : '/images/logo-white.svg';
    const { selectedOfficeSpace } = useOfficeSpaceStore();
    const { user, getCurrentUser } = useAuthStore();
    const isCompact  = useMediaQuery('(max-width: 1100px)');
    const isMobileLayout = useMediaQuery('(max-width: 768px)');
    const [orientation, setOrientation] = useState('portrait');
    // Smooth fade + gentle zoom on open (matches the crop / preview editors' entrance).
    const [entered, setEntered] = useState(false);
    useEffect(() => {
        if (!opened) { setEntered(false); return undefined; }
        const id = requestAnimationFrame(() => requestAnimationFrame(() => setEntered(true)));
        return () => cancelAnimationFrame(id);
    }, [opened]);
    // Photo gap default comes from the user's saved profile setting (defaultImageGap),
    // falling back to 3mm for everyone who hasn't customised it. Reset to that default
    // on each open; a manual slider drag (gapTouchedRef) keeps the in-session value.
    const [imageGap, setImageGap] = useState(() => useAuthStore.getState().user?.settings?.defaultImageGap ?? 3);
    const gapTouchedRef = useRef(false);

    // AI-template generated images (image ref "t<id>") must open FREE-size from
    // EVERY launch point — documents page, dashboard, preview modal — even when
    // the caller didn't pass initialSizeType. Everything else keeps the caller's
    // choice (default: passport).
    const launchedIsTemplateImage = /^t\d+$/.test(String(imageId ?? ''));
    const effInitialSizeType = launchedIsTemplateImage ? 'free' : initialSizeType;
    // The slider beside the sheet lists the images nearby — of the SAME kind as the one opened: a manual edit (m…) between the
    // manual edits, a Studio Editor photo (s…) between the studio photos, an AI template (t…) between the templates, an AI photo between the AI photos. (The default history feed only
    // holds regular AI photos, so a manual / template image used to sit between unrelated AI photos.) The kind is read off the
    // image id — unless the caller says it with `historySource` ('manual' | 'ai-templates'): the live edit printed from the manual
    // editor is a rendered blob with no id yet, yet it still belongs among the manual edits.
    const historySource = historySourceProp || (/^m\d+$/.test(String(imageId ?? '')) ? 'manual' : /^s\d+$/.test(String(imageId ?? '')) ? 'studio' : launchedIsTemplateImage ? 'ai-templates' : null);

    // ── Output page size ───────────────────────────────────────────────────────
    // The selected sheet size. Defaults to A4 so existing composite-sheet behaviour
    // is unchanged. `custom` uses customPageW/customPageH (millimetres).
    const [pageSizeKey, setPageSizeKey] = useState('a4');
    const [customPageW, setCustomPageW] = useState(100);
    const [customPageH, setCustomPageH] = useState(150);
    const [isDownloading, setIsDownloading] = useState(false);

    // ── Advanced options: attach a customer QR code to the sheet ────────────────

    // Portrait-oriented sheet dimensions for the current selection…
    const basePageDims = (() => {
        if (pageSizeKey === 'custom') {
            const w = Math.max(20, Math.min(Number(customPageW) || 20, 1000));
            const h = Math.max(20, Math.min(Number(customPageH) || 20, 1000));
            return { w, h };
        }
        const p = PAGE_SIZES.find(s => s.key === pageSizeKey) || PAGE_SIZES[0];
        return { w: p.w, h: p.h };
    })();
    // …swapped for landscape. All layout maths below use pageW/pageH instead of the
    // former hard-coded A4 210/297.
    const pageW = orientation === 'portrait' ? basePageDims.w : basePageDims.h;
    const pageH = orientation === 'portrait' ? basePageDims.h : basePageDims.w;

    const printRef = useRef(null);
    const previewAreaRef = useRef(null);
    const [paperScale, setPaperScale] = useState(1);

    // Active image for printing (slider selection)
    const [activeImageIndex, setActiveImageIndex] = useState(0);

    // Slider search — filters `allImages` by name so the user can jump straight to
    // an old image instead of clicking through the carousel one by one.
    const [imageQuery, setImageQuery] = useState('');
    const [searchFocused, setSearchFocused] = useState(false);
    const searchInputRef = useRef(null);
    // The module the slider lists, picked from the search box's dropdown; null = the opened image's own module.
    const [moduleKey, setModuleKey] = useState(null);
    const [moduleLoading, setModuleLoading] = useState(false);

    // Prevents double-print and double-count when user clicks rapidly
    const [isPrinting, setIsPrinting] = useState(false);

    // Cutting border toggle
    const [showCuttingBorder, setShowCuttingBorder] = useState(false);
    const [showImageBorder, setShowImageBorder] = useState(false);

    // Free-size mode — shows corner handles on every placed image so it can be
    // resized freely by dragging any corner.
    const [freeResize, setFreeResize] = useState(false);

    // Rotation state
    const [globalRotation, setGlobalRotation] = useState(0);


    // Zoom & pan for the preview canvas
    const [userZoom, setUserZoom] = useState(1); // default 100%
    const [isPanning, setIsPanning] = useState(false);
    const panRef = useRef(null);

    // ── Multi-select + marquee drag ────────────────────────────────────────────
    // `selectedIds` is the set of image ids currently highlighted. `marquee` holds
    // the in-progress selection rectangle in paper-local millimetres so it can be
    // compared directly against each image's `{x, y, width, height}` fields.
    const [selectedIds, setSelectedIds] = useState(() => new Set());
    const [marquee, setMarquee] = useState(null);
    // Mouse position where the marquee started (client coords) + whether the
    // pointer has moved enough to actually start drawing a rectangle. Below 3 px
    // we treat the gesture as a plain click on empty paper (which just clears).
    const marqueeOriginRef = useRef(null);
    const marqueeMovedRef = useRef(false);
    const MARQUEE_MIN_PX = 3;

    // Reset the session whenever the modal closes: clear the selection AND the
    // images placed on the sheet, so the next open starts fresh (the init effect
    // only seeds `images` when it's empty — without this, the previous image
    // would still sit on the paper on reopen).
    // `allImages` (the slider list) must be cleared too: it survives across
    // sessions, and `activeImageSrc` prefers `allImages[index]` over the fresh
    // `imageUrl` prop — so a reopen with a NEW image would seed the sheet with
    // LAST session's image while the history refetch is still in flight.
    useEffect(() => {
        if (!opened) {
            setSelectedIds(new Set());
            setMarquee(null);
            setImages([]);
            setAllImages([]);
            setPhotoSizeKnown(false);
            setActiveImageIndex(0);
            setImageQuery('');
            setModuleKey(null);
            knownBySrcRef.current = new Map();
            setFreeResize(false);
            // Don't leak dual/single detection into the next session — it made a
            // single image opened after a dual one seed at dual size (and vice versa).
            setIsActuallyDual(isDualImage);
        } else if (effInitialSizeType === 'free') {
            // AI-template images open at their own (free) size — pre-enable the
            // ফ্রি সাইজ toggle so the corner-resize handles are ready immediately.
            setFreeResize(true);
        }
    }, [opened]); // eslint-disable-line react-hooks/exhaustive-deps

    // Esc clears the current selection first; if nothing is selected, it closes the modal.
    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (selectedIds.size > 0) {
                setSelectedIds(new Set());
            } else {
                onClose?.();
            }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [opened, selectedIds, onClose]);

    // Shift+Arrow nudges selected images by 1 screen pixel in paper-mm space.
    useEffect(() => {
        if (!opened) return undefined;
        const onKey = (e) => {
            if (!e.shiftKey) return;
            if (!['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) return;
            if (selectedIds.size === 0) return;
            e.preventDefault();

            // Convert 1 screen pixel → mm in paper coordinate space
            const mmPerPixel = printRef.current
                ? (() => {
                    const rect = printRef.current.getBoundingClientRect();
                    return pageH / rect.height;
                  })()
                : 0.265 / paperScale;

            const step = mmPerPixel;
            const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
            const dy = e.key === 'ArrowUp'   ? -step : e.key === 'ArrowDown'  ? step : 0;

            setImages(prev => {
                const { width: maxWidth, height: maxHeight } = getA4Dimensions();
                return prev.map(img => {
                    if (!selectedIds.has(img.id)) return img;
                    const d = effectiveDims(img);
                    const minPos = getMinGridPos(img);
                    return {
                        ...img,
                        x: Math.max(minPos, Math.min(img.x + dx, maxWidth  - d.w - minPos)),
                        y: Math.max(minPos, Math.min(img.y + dy, maxHeight - d.h - minPos)),
                    };
                });
            });
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, selectedIds, orientation, imageGap, paperScale, pageW, pageH]);

    // Full image list for the slider — fetched fresh when modal opens
    const [allImages, setAllImages] = useState([]);
    // True once the opened photo's saved size is known, so the sheet starts at that size.
    const [photoSizeKnown, setPhotoSizeKnown] = useState(false);

    // The slider's list from a history feed: the opened image first (when `withCurrent`), then the feed's images.
    const buildList = (history, withCurrent = true) => {
            const list = [];
            if (imageUrl && withCurrent) {
                // The current image carries no background prop — look it up in history by id/src
                // so a transparent cut-out still prints on its stored colour.
                const curMatch = (history || []).find(h => (imageId != null && h.id === imageId) || h.aiImage === imageUrl);
                list.push({ id: imageId || 'current', src: imageUrl, name: curMatch?.fileName || '', createdAt: null, background: curMatch?.background ?? null, photoSize: curMatch?.photoSize ?? null });
            }
            (history || []).forEach(h => {
                // Skip the history entry that's already shown as the current image. Match on
                // id first: the current image may be an edited `blob:` URL (from the right-panel
                // print button) whose src won't equal the history's aiImage, so a src-only check
                // would let the same image slip in twice. Fall back to src for entries with no id.
                const isCurrent = (imageId != null && h.id === imageId) || h.aiImage === imageUrl;
                if (h.aiImage && (!isCurrent || !withCurrent)) {
                    list.push({ id: h.id, src: h.aiImage, name: h.fileName || '', createdAt: h.createdAt || h.timestamp || null, background: h.background ?? null, photoSize: h.photoSize ?? null });
                }
            });
            return list;
    };

    useEffect(() => {
        if (!opened) return;

        // The "all" history feed (source=null) returns ONLY regular AI photos
        // (GeneratedImage). Manual (m…) and AI-template (t…) images live in their own
        // tables and are absent here — so a manual/template cut-out opened into this
        // modal has NO background token in `list`, and its transparent subject would
        // print/export with no colour. Fetch that one image's metadata by id and patch
        // its entry so the stored bg composites everywhere (preview, print, PDF).
        const finalize = (list) => {
            setAllImages(list);
            const idx = list.findIndex(img => img.id === imageId || img.src === imageUrl);
            setActiveImageIndex(idx >= 0 ? idx : 0);
            const cur = list.find(img => img.id === imageId || img.src === imageUrl);
            if (imageId && imageId !== 'current' && cur && (cur.background == null || cur.photoSize == null)) {
                imageGenerationService.getImageById(imageId)
                    .then(res => {
                        const bg = res?.data?.background ?? res?.background ?? null;
                        const photoSize = res?.data?.photoSize ?? res?.photoSize ?? null;
                        if (bg == null && photoSize == null) return;
                        setAllImages(prev => prev.map(a => (a.src === cur.src ? { ...a, background: a.background ?? bg, photoSize: a.photoSize ?? photoSize } : a)));
                    })
                    .catch(() => {})
                    .finally(() => setPhotoSizeKnown(true));
            } else {
                setPhotoSizeKnown(true);
            }
        };

        // Fetch all images with a high limit so the slider shows everything
        imageGenerationService.getHistory({ page: 1, limit: 500, officeSpaceId: selectedOfficeSpace?.id, ...(historySource ? { source: historySource } : {}) })
            .then(response => {
                const history = response.data?.history || [];
                finalize(buildList(history));
            })
            .catch(() => {
                // Fallback to the prop if fetch fails
                finalize(buildList(imageHistory));
            });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, imageUrl, imageId]);

    const clampedIndex = Math.min(activeImageIndex, Math.max(0, allImages.length - 1));
    const activeImageSrc = allImages[clampedIndex]?.src || imageUrl;
    const activeImageId  = allImages[clampedIndex]?.id  || imageId;
    const activePhotoSize = allImages[clampedIndex]?.photoSize ?? null;

    // src → stored background token, so placed print items (which reference only `src`)
    // can composite the right colour behind transparent cut-outs (preview + export).
    // Every image the slider has listed this session (src → entry), so photos already on the sheet keep their background and
    // print id after the slider switches to another module.
    const knownBySrcRef = useRef(new Map());
    const bgBySrc = useMemo(() => {
        allImages.forEach(a => { if (a.src) knownBySrcRef.current.set(a.src, a); });
        const m = new Map();
        knownBySrcRef.current.forEach((a, src) => m.set(src, a.background ?? null));
        return m;
    }, [allImages]);

    // The module the slider shows, and switching it from the search box's dropdown.
    const launchModuleKey = PRINT_MODULES.find((m) => m.source === historySource)?.key || 'ai';
    const activeModule = PRINT_MODULES.find((m) => m.key === (moduleKey || launchModuleKey)) || PRINT_MODULES[0];
    const switchModule = (key) => {
        setImageQuery('');
        setSearchFocused(false);
        if (key === activeModule.key || moduleLoading) return;
        const mod = PRINT_MODULES.find((m) => m.key === key);
        setModuleKey(key);
        setModuleLoading(true);
        const load = key === 'bulk'
            ? loadBulkPhotos(selectedOfficeSpace?.id)
            : imageGenerationService.getHistory({ page: 1, limit: 500, officeSpaceId: selectedOfficeSpace?.id, ...(mod.source ? { source: mod.source } : {}) })
                // The opened image stays first when the slider comes back to its own module.
                .then((response) => buildList(response.data?.history || [], key === launchModuleKey));
        load
            .then((list) => {
                setAllImages(list);
                setActiveImageIndex(0);
            })
            .catch(() => notifications.show({ message: t('imageGeneration.printModuleLoadFailed'), color: 'red' }))
            .finally(() => setModuleLoading(false));
    };

    // CSS background style for a print cell whose photo is a transparent cut-out.
    // `print-color-adjust: exact` keeps the colour when printing even if the user
    // leaves the browser's "Background graphics" option off (Chrome's default).
    const PRINT_BG_ADJUST = { WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' };
    const printBgStyleFor = (src) => {
        const token = bgBySrc.get(src);
        if (!token || token === 'transparent') return {};
        if (isSpecialBgToken(token)) {
            const css = specialBgCss(token);
            return css ? { ...css, ...PRINT_BG_ADJUST } : {};
        }
        const c = resolveSolidBg(token);
        return c ? { background: c, ...PRINT_BG_ADJUST } : {};
    };

    // Name-matched slider results (keeps each image's real index so a click jumps
    // the carousel to it). Empty query → no dropdown.
    const trimmedQuery = imageQuery.trim().toLowerCase();
    const searchResults = trimmedQuery
        ? allImages
            .map((img, idx) => ({ img, idx }))
            .filter(({ img }) => (img.name || '').toLowerCase().includes(trimmedQuery))
        : [];

    // Compute scale to fit the A4 paper inside the preview area.
    // Capped at 100% (never zoom-in beyond actual size).
    // No hard lower-bound — lets it shrink as far as needed on tiny screens.
    const updatePaperScale = useCallback(() => {
        if (!previewAreaRef.current) return;
        const { clientWidth, clientHeight } = previewAreaRef.current;
        if (!clientWidth || !clientHeight) return; // not yet laid out
        // Reserve the preview padding on each side so the sheet keeps a visible
        // margin. Mobile uses a larger inset (matches .previewArea padding below).
        const inset = clientWidth <= 768 ? 20 : 10;
        const availW = clientWidth  - inset * 2;
        const availH = clientHeight - inset * 2;
        const pxPerMm = 96 / 25.4;
        const paperW = pageW * pxPerMm;
        const paperH = pageH * pxPerMm;
        // Fit-to-area, then cap upscale at 3× so small photo sheets (2R/3R/4R…) fill the
        // preview instead of showing tiny, without blowing the raster up too far. Large
        // document sheets (A4) always fit below 1× so the cap never affects them.
        const fit      = Math.min(availW / paperW, availH / paperH);
        const scale    = Math.min(fit, 3);
        const newScale = Math.max(scale, 0.05); // hard floor so paper is never invisible
        setPaperScale(newScale);
    }, [pageW, pageH]);

    // Run on orientation change AND whenever the modal opens so the ref is ready
    useEffect(() => {
        if (!opened) return;
        setUserZoom(1); // reset to 100% on open
        // Small delay lets the fullscreen modal finish painting before we measure
        const t = setTimeout(updatePaperScale, 60);
        const observer = new ResizeObserver(updatePaperScale);
        if (previewAreaRef.current) observer.observe(previewAreaRef.current);
        return () => { clearTimeout(t); observer.disconnect(); };
    }, [updatePaperScale, opened]);

    // effectiveScale: user's manual zoom if set, otherwise auto-fit
    const effectiveScale = userZoom ?? paperScale;

    const zoomIn  = () => setUserZoom(prev => Math.min((prev ?? paperScale) * 1.25, 4));
    const zoomOut = () => setUserZoom(prev => Math.max((prev ?? paperScale) / 1.25, 0.1));
    const fitPage = () => setUserZoom(paperScale);

    // Attach wheel listener as non-passive so preventDefault() works for Ctrl+scroll zoom
    useEffect(() => {
        const el = previewAreaRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();
                const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;
                setUserZoom(prev => Math.min(Math.max((prev ?? paperScale) * factor, 0.1), 4));
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paperScale]);

    const handlePreviewPanStart = (e) => {
        if (e.button !== 0) return;
        if (e.target.closest('#printable-page')) return;
        panRef.current = {
            x: e.clientX,
            y: e.clientY,
            scrollLeft: previewAreaRef.current?.scrollLeft || 0,
            scrollTop:  previewAreaRef.current?.scrollTop  || 0,
        };
        setIsPanning(true);
    };

    const handlePreviewPanMove = (e) => {
        if (!panRef.current || !previewAreaRef.current) return;
        previewAreaRef.current.scrollLeft = panRef.current.scrollLeft - (e.clientX - panRef.current.x);
        previewAreaRef.current.scrollTop  = panRef.current.scrollTop  - (e.clientY - panRef.current.y);
    };

    const handlePreviewPanEnd = () => {
        panRef.current = null;
        setIsPanning(false);
    };

    // Auto-detect dual from image aspect ratio (catches history images where isDualImage prop may be false)
    const [isActuallyDual, setIsActuallyDual] = useState(isDualImage);
    useEffect(() => { setIsActuallyDual(isDualImage); }, [isDualImage]);

    // Gap is internal to each image slot (subtracted from DB size).
    // The imageGrid is flush with the paper edge; the slot's halfGap provides the margin.
    const pagePadding = 0;

    // Get the sheet's usable dimensions based on the selected page size + orientation
    // (minus dynamic page padding). Named for historical reasons; no longer A4-only.
    const getA4Dimensions = () => {
        const width  = pageW - 2 * pagePadding;
        const height = pageH - 2 * pagePadding;
        return { width, height };
    };

    // Get the effective (visually rendered) width/height of an image accounting for rotation
    const effectiveDims = (img) => {
        const rot = img.rotation || 0;
        const transposed = rot === 90 || rot === 270;
        return {
            w: transposed ? img.height : img.width,
            h: transposed ? img.width  : img.height,
        };
    };

    // Gap is now internal to each slot — no extra space added between images in packing.
    const getEffectiveGap = (_img) => 0;

    // Gap required between a specific pair of images: always 0 (gap is internal).
    const pairGap = (_img1, _img2) => 0;

    // All images can start at the grid origin (paper edge).
    // Stamps are kept a step (imageGap/4) IN from the page edge during placement, so an
    // edge stamp's photo (which is itself inset imageGap/4) lands imageGap/2 from the page
    // — the FULL gap, matching the passport — WITHOUT shrinking the photo or touching the
    // even gaps between stamps. Passports already sit imageGap/2 from the edge, so margin 0.
    const getMinGridPos = (img) => (img?.sizeType === 'stamp' ? imageGap / 4 : 0);

    // Toggle an image's membership in the multi-select — wired to Ctrl/Cmd-click
    // inside DraggableImageItem.
    const toggleSelect = (id) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    // Convert a client (screen) pixel coordinate to imageGrid-space mm.
    // The imageGrid sits pagePadding mm inside the paper on all sides, so we
    // subtract that offset so that (0,0) = top-left of the usable image area.
    const clientToPaperMm = (clientX, clientY) => {
        if (!printRef.current) return null;
        const rect = printRef.current.getBoundingClientRect();
        const paperWidthMm  = pageW;
        const paperHeightMm = pageH;
        const pxPerMmX = rect.width  / paperWidthMm;
        const pxPerMmY = rect.height / paperHeightMm;
        const xMm = (clientX - rect.left) / pxPerMmX - pagePadding;
        const yMm = (clientY - rect.top)  / pxPerMmY - pagePadding;
        return { x: xMm, y: yMm };
    };

    // ── Marquee drag handlers ─────────────────────────────────────────────────
    // Started on the paper container; only fires when the pointer lands on empty
    // paper (dnd-kit already owns pointerdown on images themselves because those
    // draggables stop propagation internally via their own sensors).
    const handlePaperMouseDown = (e) => {
        if (e.button !== 0) return; // left-click only
        // If the mousedown started on an image, let dnd-kit drag handle it.
        if (e.target.closest('[data-print-item]')) return;
        const start = clientToPaperMm(e.clientX, e.clientY);
        if (!start) return;
        marqueeOriginRef.current = { clientX: e.clientX, clientY: e.clientY, ...start };
        marqueeMovedRef.current = false;
        // Don't clear selection yet — if this ends up a plain click we'll clear,
        // but if Ctrl/Cmd is held we preserve the existing selection to allow
        // additive marquee drags.
    };

    const handlePaperMouseMove = (e) => {
        const origin = marqueeOriginRef.current;
        if (!origin) return;
        const dx = e.clientX - origin.clientX;
        const dy = e.clientY - origin.clientY;
        if (!marqueeMovedRef.current && Math.hypot(dx, dy) < MARQUEE_MIN_PX) return;
        marqueeMovedRef.current = true;
        const cur = clientToPaperMm(e.clientX, e.clientY);
        if (!cur) return;
        const box = {
            x: Math.min(origin.x, cur.x),
            y: Math.min(origin.y, cur.y),
            w: Math.abs(cur.x - origin.x),
            h: Math.abs(cur.y - origin.y),
        };
        setMarquee(box);

        // Live-recompute selection. If Ctrl/Cmd held at mousedown we add to the
        // existing selection; otherwise this marquee replaces it.
        const additive = e.ctrlKey || e.metaKey;
        setSelectedIds(prev => {
            const next = additive ? new Set(prev) : new Set();
            for (const img of images) {
                const d = effectiveDims(img);
                const intersects = !(
                    img.x + d.w < box.x ||
                    img.x       > box.x + box.w ||
                    img.y + d.h < box.y ||
                    img.y       > box.y + box.h
                );
                if (intersects) next.add(img.id);
            }
            return next;
        });
    };

    const handlePaperMouseUp = () => {
        const origin = marqueeOriginRef.current;
        marqueeOriginRef.current = null;
        setMarquee(null);
        // Plain click on empty paper (no drag past threshold) clears selection.
        if (origin && !marqueeMovedRef.current) {
            setSelectedIds(new Set());
        }
        marqueeMovedRef.current = false;
    };

    // Check if two rectangles overlap, enforcing each image type's effective gap.
    const checkOverlap = (img1, img2) => {
        const gap = pairGap(img1, img2);
        const d1 = effectiveDims(img1);
        const d2 = effectiveDims(img2);
        return !(
            img1.x + d1.w + gap <= img2.x ||
            img2.x + d2.w + gap <= img1.x ||
            img1.y + d1.h + gap <= img2.y ||
            img2.y + d2.h + gap <= img1.y
        );
    };

    // Find available position that doesn't overlap
    const findAvailablePosition = (width, height, sizeType, existingImages) => {
        const { width: maxWidth, height: maxHeight } = getA4Dimensions();
        const step = 1;
        const minPos = getMinGridPos({ sizeType });

        for (let y = minPos; y <= maxHeight - height - minPos; y += step) {
            for (let x = minPos; x <= maxWidth - width - minPos; x += step) {
                const testImage = { x, y, width, height, sizeType };
                const hasOverlap = existingImages.some(img => checkOverlap(testImage, img));
                if (!hasOverlap) return { x, y };
            }
        }

        // If no position found, return null (page is full)
        return null;
    };

    // Resolve print sizes from user profile settings, falling back to standard defaults
    const ps = user?.settings?.printSizes || {};
    const passportSingleW = ps.passportSingleW || 41;
    const passportSingleH = ps.passportSingleH || 50;
    // Dual = landscape (single passport rotated). Editable in profile settings;
    // defaults to 50×41 (passport) / 25×20 (stamp).
    const passportDualW   = ps.passportDualW   || 50;
    const passportDualH   = ps.passportDualH   || 41;
    const stampSingleW    = ps.stampSingleW    || 20;
    const stampSingleH    = ps.stampSingleH    || 25;
    const stampDualW      = ps.stampDualW      || 25;
    const stampDualH      = ps.stampDualH      || 20;
    const size2RW         = ps.size2RW         || 64;
    const size2RH         = ps.size2RH         || 89;
    const size3RW         = ps.size3RW         || 89;
    const size3RH         = ps.size3RH         || 127;
    const size4RW         = ps.size4RW         || 102;
    const size4RH         = ps.size4RH         || 152;
    const size5RW         = ps.size5RW         || 127;
    const size5RH         = ps.size5RH         || 178;
    const size6RW         = ps.size6RW         || 152;
    const size6RH         = ps.size6RH         || 203;
    const sizeA4W         = ps.sizeA4W         || 210;
    const sizeA4H         = ps.sizeA4H         || 297;

    const passportW = isActuallyDual ? passportDualW : passportSingleW;
    const passportH = isActuallyDual ? passportDualH : passportSingleH;
    const stampW    = isActuallyDual ? stampDualW    : stampSingleW;
    const stampH    = isActuallyDual ? stampDualH    : stampSingleH;

    // The first quick-add row follows the photo in the slider's centre: a visa, e-pass or birth photo adds at its own
    // print size from Settings; anything else stays passport.
    const docPrint = isActuallyDual ? null : photoPrintSizeOf(activePhotoSize);
    const mainType = docPrint?.key ?? 'passport';
    const mainW = docPrint ? photoPrintDims(docPrint, ps).w : passportW;
    const mainH = docPrint ? photoPrintDims(docPrint, ps).h : passportH;
    const mainLabel = docPrint ? t(`imageGeneration.${docPrint.labelKey}`) : t(isActuallyDual ? 'imageGeneration.abbrDual' : 'imageGeneration.passport');

    // Base dimensions per sizeType at 100% scale.
    // isDual comes from the image object itself, not the global isActuallyDual flag,
    // so mixed pages (some dual, some single) scale independently.
    const getBaseDims = (sizeType, isDual = false) => {
        switch (sizeType) {
            case 'passport': return { w: isDual ? passportDualW : passportSingleW, h: isDual ? passportDualH : passportSingleH };
            case 'stamp':    return { w: isDual ? stampDualW    : stampSingleW,    h: isDual ? stampDualH    : stampSingleH    };
            case '2r':       return { w: size2RW, h: size2RH };
            case '3r':       return { w: size3RW, h: size3RH };
            case '4r':       return { w: size4RW, h: size4RH };
            case '5r':       return { w: size5RW, h: size5RH };
            case '6r':       return { w: size6RW, h: size6RH };
            case 'a4':       return { w: sizeA4W, h: sizeA4H };
            default: {
                const doc = photoPrintSizeOf(sizeType);
                return doc ? photoPrintDims(doc, ps) : null;
            }
        }
    };

    // Calculate initial positions for images
    const calculateInitialPosition = (isStamp, existingImages) => {
        const width    = isStamp ? stampW : passportW;
        const height   = isStamp ? stampH : passportH;
        const sizeType = isStamp ? 'stamp' : 'passport';
        return findAvailablePosition(width, height, sizeType, existingImages);
    };

    // ── Max-count packer (rotation allowed) ─────────────────────────────────────
    // Packs images to fit the MOST per page. Larger first; each image tries BOTH
    // orientations — its current one and the 90° transpose — and takes the best slot
    // (lowest y, then lowest x), preferring its current orientation on a tie so a photo
    // only turns sideways when that actually lets it drop into a lower/leftmost gap or
    // fills a leftover strip. Runs from two seeds (as-is and all-transposed) and keeps
    // whichever PLACES MORE, so a uniform sheet also flips to the denser orientation.
    // The transpose is 0↔90 (never 180/270), so photos never come out upside-down.
    // Reuses checkOverlap + page bounds, so nothing overlaps or leaves the page.
    const transposeRot = (r) => (((r || 0) % 180) === 0 ? 90 : 0);
    const packForMaxCount = (list) => {
        const { width: maxWidth, height: maxHeight } = getA4Dimensions();
        const runPack = (items) => {
            const sorted = [...items].sort((a, b) => {
                const da = effectiveDims(a), db = effectiveDims(b);
                return (db.w * db.h) - (da.w * da.h);
            });
            const arranged = [];
            for (const image of sorted) {
                const minPos = getMinGridPos(image);
                // Candidate grid lines = page origin + every placed image's far edges.
                const ys = new Set([minPos]);
                const xs = new Set([minPos]);
                arranged.forEach(img => {
                    const d = effectiveDims(img);
                    ys.add(img.y + d.h + getEffectiveGap(img));
                    xs.add(img.x + d.w + getEffectiveGap(img));
                });
                const ysArr = Array.from(ys).sort((a, b) => a - b);
                const xsArr = Array.from(xs).sort((a, b) => a - b);
                const baseRot = image.rotation || 0;
                const rots = [baseRot, transposeRot(baseRot)];
                let best = null; // { x, y, rot }
                for (const y of ysArr) {
                    if (best && y > best.y) break; // ascending — no lower y possible now
                    for (const rot of rots) {
                        const d = effectiveDims({ ...image, rotation: rot });
                        if (y + d.h > maxHeight - minPos) continue;
                        for (const x of xsArr) {
                            if (best && y === best.y && x >= best.x) break; // can't beat best.x here
                            if (x + d.w > maxWidth - minPos) continue;
                            const test = { ...image, rotation: rot, x, y };
                            if (!arranged.some(img => checkOverlap(test, img))) {
                                if (!best || y < best.y || (y === best.y && x < best.x) ||
                                    (y === best.y && x === best.x && rot === baseRot)) {
                                    best = { x, y, rot };
                                }
                                break; // first (smallest) x that fits for this (rot, y)
                            }
                        }
                    }
                }
                if (best) {
                    arranged.push({ ...image, rotation: best.rot, x: best.x, y: best.y });
                } else {
                    // No edge slot — stack beneath everything, un-rotated, if it still
                    // fits the sheet height (mirrors the previous fallback).
                    const d = effectiveDims(image);
                    const bottomY = arranged.length
                        ? Math.max(...arranged.map(img => img.y + effectiveDims(img).h)) + getEffectiveGap(image)
                        : minPos;
                    if (bottomY + d.h <= maxHeight - minPos) {
                        arranged.push({ ...image, x: minPos, y: bottomY });
                    }
                }
            }
            return arranged;
        };
        const asIs    = runPack(list);
        const flipped = runPack(list.map(im => ({ ...im, rotation: transposeRot(im.rotation || 0) })));
        return flipped.length > asIs.length ? flipped : asIs;
    };

    // Auto-arrange all images to fit the MOST per page (rotates individual images when
    // it helps — see packForMaxCount).
    const autoArrangeImages = () => setImages(packForMaxCount(images));

    // Pure version of autoArrangeImages — takes a list, returns the arranged list.
    // Used inside setImages callbacks where React state isn't updated yet.
    const arrangeList = (list) => {
        const { width: maxWidth, height: maxHeight } = getA4Dimensions();
        const sorted = [...list].sort((a, b) => {
            const da = effectiveDims(a);
            const db = effectiveDims(b);
            return (db.w * db.h) - (da.w * da.h);
        });
        const arranged = [];
        const getCandidates = (placed, currentImage) => {
            const minPos = getMinGridPos(currentImage);
            const ys = new Set([minPos]);
            const xs = new Set([minPos]);
            placed.forEach(img => {
                const d = effectiveDims(img);
                const g = getEffectiveGap(img);
                ys.add(img.y + d.h + g);
                xs.add(img.x + d.w + g);
            });
            return {
                ys: Array.from(ys).sort((a, b) => a - b),
                xs: Array.from(xs).sort((a, b) => a - b),
            };
        };
        for (const image of sorted) {
            let placed = false;
            const { ys, xs } = getCandidates(arranged, image);
            const d = effectiveDims(image);
            const minPos = getMinGridPos(image);
            for (const y of ys) {
                if (y + d.h > maxHeight - minPos) continue;
                if (placed) break;
                for (const x of xs) {
                    if (x + d.w > maxWidth - minPos) continue;
                    const test = { ...image, x, y };
                    if (!arranged.some(img => checkOverlap(test, img))) {
                        arranged.push(test);
                        placed = true;
                        break;
                    }
                }
            }
            if (!placed) {
                const bottomY = arranged.length > 0
                    ? Math.max(...arranged.map(img => img.y + effectiveDims(img).h)) + getEffectiveGap(image)
                    : minPos;
                if (bottomY + d.h <= maxHeight - minPos) arranged.push({ ...image, x: minPos, y: bottomY });
            }
        }
        return arranged;
    };

    // The packer always packs to the LEFT. For center/right alignment, shift the WHOLE
    // packed block by ONE common offset so the arrangement is preserved exactly. Per-row
    // shifting breaks masonry layouts (mixed passport/stamp sizes): a tall image spans
    // multiple y-rows, so shifting a shorter row independently overlaps it. Moving every
    // image by the same amount keeps all relative positions — no collisions.
    const alignRows = (list, align) => {
        if (align === 'left' || !list.length) return list;
        const { width: maxWidth } = getA4Dimensions();
        const rightEdge = Math.max(...list.map(img => img.x + effectiveDims(img).w));
        const margin = Math.min(...list.map(img => getMinGridPos(img)));
        const free = (maxWidth - margin) - rightEdge;
        if (free <= 0) return list;
        const shift = align === 'center' ? free / 2 : free;
        return list.map(img => ({ ...img, x: img.x + shift }));
    };

    // Auto-arrange everything (max-count, rotation allowed), then align the packed
    // rows (dropdown options) — same packer as the main Auto-Arrange button.
    const autoArrangeAligned = (align) => {
        setImages(prev => alignRows(packForMaxCount(prev), align));
    };

    // Re-pack only the currently multi-selected images with the active `imageGap`,
    // preserving their reading-order and using their current top-left corner as
    // the anchor. Non-selected images are left untouched so the rest of the page
    // layout stays where the admin put it.
    const autoArrangeSelection = () => {
        if (selectedIds.size < 2) return;
        setImages(prev => {
            const selected = prev.filter(img => selectedIds.has(img.id));
            if (selected.length < 2) return prev;

            const { width: maxWidth, height: maxHeight } = getA4Dimensions();
            const anchorX = Math.min(...selected.map(img => img.x));
            const anchorY = Math.min(...selected.map(img => img.y));

            // Sort by reading order so the re-pack roughly matches what the
            // admin expected to see.
            const sorted = [...selected].sort((a, b) => {
                if (Math.abs(a.y - b.y) > 5) return a.y - b.y;
                return a.x - b.x;
            });

            const placedById = new Map();
            let rowX = anchorX;
            let rowY = anchorY;
            let rowHeight = 0;
            let rowGap = 0; // tracks the largest effective gap in the current row for y advancement

            for (const img of sorted) {
                const d = effectiveDims(img);
                const g = getEffectiveGap(img);
                if (rowX + d.w > maxWidth) {
                    rowX = anchorX;
                    rowY = rowY + rowHeight + rowGap;
                    rowHeight = 0;
                    rowGap = 0;
                }
                const minPos = getMinGridPos(img);
                const nx = Math.max(minPos, Math.min(rowX, maxWidth  - d.w - minPos));
                const ny = Math.max(minPos, Math.min(rowY, maxHeight - d.h - minPos));
                placedById.set(img.id, { ...img, x: nx, y: ny });
                rowX = rowX + d.w + g;
                rowHeight = Math.max(rowHeight, d.h);
                rowGap = Math.max(rowGap, g);
            }

            return prev.map(img => placedById.get(img.id) || img);
        });
    };

    // Find snap position when dragging near another image (5mm threshold)
    const findSnapPosition = (draggedImage, otherImages) => {
        const { width: maxWidth, height: maxHeight } = getA4Dimensions();
        const snapThreshold = 5;

        const dd = effectiveDims(draggedImage);
        let snapX = draggedImage.x;
        let snapY = draggedImage.y;
        let closestXDiff = Infinity;
        let closestYDiff = Infinity;

        for (const otherImage of otherImages) {
            const od = effectiveDims(otherImage);
            const gap = pairGap(draggedImage, otherImage); // each pair uses its own effective gap

            const snapCandidatesY = [
                { pos: otherImage.y,                                    diff: Math.abs(draggedImage.y - otherImage.y) },
                { pos: otherImage.y + od.h - dd.h,                      diff: Math.abs((draggedImage.y + dd.h) - (otherImage.y + od.h)) },
                { pos: otherImage.y + od.h + gap,                       diff: Math.abs(draggedImage.y - (otherImage.y + od.h + gap)) },
                { pos: otherImage.y - dd.h - gap,                       diff: Math.abs(draggedImage.y - (otherImage.y - dd.h - gap)) },
            ];

            for (const candidate of snapCandidatesY) {
                if (candidate.pos >= 0 && candidate.diff <= snapThreshold && candidate.diff < closestYDiff) {
                    snapY = candidate.pos;
                    closestYDiff = candidate.diff;
                }
            }

            const snapCandidatesX = [
                { pos: otherImage.x,                                    diff: Math.abs(draggedImage.x - otherImage.x) },
                { pos: otherImage.x + od.w - dd.w,                      diff: Math.abs((draggedImage.x + dd.w) - (otherImage.x + od.w)) },
                { pos: otherImage.x + od.w + gap,                       diff: Math.abs(draggedImage.x - (otherImage.x + od.w + gap)) },
                { pos: otherImage.x - dd.w - gap,                       diff: Math.abs(draggedImage.x - (otherImage.x - dd.w - gap)) },
            ];

            for (const candidate of snapCandidatesX) {
                if (candidate.pos >= 0 && candidate.diff <= snapThreshold && candidate.diff < closestXDiff) {
                    snapX = candidate.pos;
                    closestXDiff = candidate.diff;
                }
            }
        }

        // Snap to the PAGE edges too (respecting the stamp page-margin), so an image can be
        // aligned to the paper boundary — not only to other images' edges. This lets a corner
        // image reach the edge instead of getting stuck aligned to a far-away block.
        const snapMinPos = getMinGridPos(draggedImage);
        const pageEdgeX = [
            { pos: snapMinPos,                diff: Math.abs(draggedImage.x - snapMinPos) },
            { pos: maxWidth - dd.w - snapMinPos, diff: Math.abs(draggedImage.x - (maxWidth - dd.w - snapMinPos)) },
        ];
        for (const c of pageEdgeX) {
            if (c.diff <= snapThreshold && c.diff < closestXDiff) { snapX = c.pos; closestXDiff = c.diff; }
        }
        const pageEdgeY = [
            { pos: snapMinPos,                 diff: Math.abs(draggedImage.y - snapMinPos) },
            { pos: maxHeight - dd.h - snapMinPos, diff: Math.abs(draggedImage.y - (maxHeight - dd.h - snapMinPos)) },
        ];
        for (const c of pageEdgeY) {
            if (c.diff <= snapThreshold && c.diff < closestYDiff) { snapY = c.pos; closestYDiff = c.diff; }
        }

        snapX = Math.max(snapMinPos, Math.min(snapX, maxWidth  - dd.w - snapMinPos));
        snapY = Math.max(snapMinPos, Math.min(snapY, maxHeight - dd.h - snapMinPos));

        return { x: snapX, y: snapY, snapped: closestXDiff < Infinity || closestYDiff < Infinity };
    };

    const [images, setImages] = useState([]);

    // Fetch fresh profile settings when modal opens so sizes are always up-to-date.
    // Also clear the "user dragged the gap slider" flag so a fresh open starts from
    // the saved default again.
    useEffect(() => {
        if (opened) {
            gapTouchedRef.current = false;
            getCurrentUser().catch(() => {});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened]);

    // Apply the saved default gap on open and when the fresh settings arrive — but
    // stop once the user has dragged the slider, so their in-session choice sticks.
    useEffect(() => {
        if (opened && !gapTouchedRef.current) {
            setImageGap(user?.settings?.defaultImageGap ?? 3);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, user?.settings?.defaultImageGap]);

    // Initialize images when modal opens or active image changes
    useEffect(() => {
        if (opened && activeImageSrc && images.length === 0) {
            // FREE size (AI-template images): keep the image's OWN aspect ratio,
            // fitted inside the printable page and centred — not a stretched A4.
            // A photo made for a print size (the Studio Editor's 2R … A4) passes
            // `initialSizeMm` and lands at exactly that size, shrunk only if the
            // sheet is smaller. The user can then corner-resize it via the ফ্রি সাইজ toggle.
            if (effInitialSizeType === 'free') {
                let cancelled = false;
                const im = new window.Image();
                im.onload = () => {
                    if (cancelled) return;
                    const areaW = pageW - 2 * pagePadding;
                    const areaH = pageH - 2 * pagePadding;
                    const sized = initialSizeMm?.w > 0 && initialSizeMm?.h > 0;
                    const ratio = sized ? initialSizeMm.h / initialSizeMm.w : (im.naturalHeight || 1) / (im.naturalWidth || 1);
                    let w = sized ? Math.min(initialSizeMm.w, areaW) : areaW, h = w * ratio;
                    if (h > areaH) { h = areaH; w = h / ratio; }
                    setImages(prev => prev.length ? prev : [{
                        id: 'img-1',
                        src: activeImageSrc,
                        width: w,
                        height: h,
                        x: Math.max(0, (areaW - w) / 2),
                        y: Math.max(0, (areaH - h) / 2),
                        isStamp: false,
                        sizeType: 'free',
                        rotation: globalRotation,
                        isDual: false,
                    }]);
                };
                im.src = activeImageSrc;
                return () => { cancelled = true; };
            }
            // The seed takes the photo's own print size (visa, e-pass, birth).
            if (effInitialSizeType !== 'a4' && !photoSizeKnown) return undefined;
            // Passport/A4 seeding needs the image's REAL pixel aspect ratio to pick
            // single vs DUAL sizes (dual photos are landscape). Deciding from the
            // async carousel detection raced the seed on FIRST open — the sheet
            // filled with single-width slots showing squeezed dual photos.
            let cancelled = false;
            const im = new window.Image();
            im.onload = () => {
                if (cancelled) return;
                const freshPs = useAuthStore.getState().user?.settings?.printSizes || {};
                const a4 = effInitialSizeType === 'a4';
                const dual = !a4 && (isDualImage || im.naturalWidth / im.naturalHeight > 1.1);
                // Sync the global flag so the quick-add buttons match immediately.
                if (!a4) setIsActuallyDual(dual);
                const doc = a4 || dual ? null : photoPrintSizeOf(activePhotoSize);
                const docDims = doc && photoPrintDims(doc, freshPs);
                const seedType = a4 ? 'a4' : doc ? doc.key : 'passport';
                const fw = a4 ? (freshPs.sizeA4W || 210) : doc ? docDims.w : dual ? (freshPs.passportDualW || 50) : (freshPs.passportSingleW || 41);
                const fh = a4 ? (freshPs.sizeA4H || 297) : doc ? docDims.h : dual ? (freshPs.passportDualH || 41) : (freshPs.passportSingleH || 50);
                const makeImage = (i, pos) => ({
                    id: `img-${i + 1}`,
                    src: activeImageSrc,
                    width: fw,
                    height: fh,
                    x: pos.x,
                    y: pos.y,
                    isStamp: false,
                    sizeType: seedType,
                    rotation: globalRotation,
                    isDual: dual,
                });
                setImages(prev => {
                    if (prev.length) return prev;
                    if (a4) return [makeImage(0, { x: 0, y: 0 })];
                    // 'a4' keeps the legacy full-page behaviour; everything else
                    // starts with FOUR prints (the common print layout).
                    const seed = [];
                    for (let i = 0; i < 4; i += 1) {
                        const pos = findAvailablePosition(fw, fh, seedType, seed);
                        if (!pos) break; // page can't fit more (very large custom sizes)
                        seed.push(makeImage(i, pos));
                    }
                    return seed.length ? seed : [makeImage(0, { x: 0, y: 0 })];
                });
            };
            im.src = activeImageSrc;
            return () => { cancelled = true; };
        }
        return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [opened, activeImageSrc, photoSizeKnown]);

    // Reset images and slider when modal closes
    useEffect(() => {
        if (!opened) {
            setImages([]);
            setAllImages([]);
            setActiveImageIndex(0);
            setImageQuery('');
            setSearchFocused(false);
            setGlobalRotation(0);
            setIsPrinting(false);
            setUserZoom(1);
        }
    }, [opened]);

    // NOTE: isActuallyDual intentionally has NO resize effect here.
    // Each image carries its own isDual flag so that a mixed page (dual + single
    // images) is stable. The onLoad handler below corrects only images whose src
    // matches the carousel center image, handling the initial detection delay.

    // Auto-arrange images when orientation OR page size changes — reset to auto-fit so
    // the paper re-scales correctly and images re-pack onto the new sheet dimensions.
    useEffect(() => {
        setUserZoom(1);
        if (images.length > 0) {
            autoArrangeImages();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [orientation, pageSizeKey, customPageW, customPageH]);

    // Auto-arrange images when gap changes. When 2+ images are multi-selected,
    // only the selected subset is re-packed with the new gap — non-selected
    // images stay exactly where they are.
    useEffect(() => {
        if (images.length === 0) return;
        if (selectedIds.size >= 2) {
            autoArrangeSelection();
        } else {
            autoArrangeImages();
        }
        // selectedIds is intentionally NOT in the deps — changing selection
        // alone should not re-pack; only gap changes do.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [imageGap]);


    const sensors = useSensors(
        useSensor(MouseSensor, {
            activationConstraint: {
                distance: 10,
            },
        }),
        useSensor(TouchSensor, {
            activationConstraint: {
                delay: 250,
                tolerance: 5,
            },
        })
    );

    // Generic handler — adds `count` images of a given size type
    const handleAddImages = (width, height, sizeType, count = 1) => {
        const scaledW = width;
        const scaledH = height;
        const newImages = [];
        for (let i = 0; i < count; i++) {
            const position = findAvailablePosition(scaledW, scaledH, sizeType, [...images, ...newImages]);
            if (!position) {
                if (newImages.length > 0) {
                    notifications.show({
                        title: t('imageGeneration.pagePartiallyFull') || 'Page Partially Full',
                        message: `${t('imageGeneration.onlyAdded') || 'Only added'} ${newImages.length} ${t('imageGeneration.outOf') || 'out of'} ${count} ${t('imageGeneration.images') || 'images'}. ${t('imageGeneration.noMoreSpace') || 'No more space available.'}`,
                        color: 'orange',
                    });
                } else {
                    notifications.show({
                        title: t('imageGeneration.pageFull') || 'Page Full',
                        message: t('imageGeneration.pageFullMessage') || 'No more space available on the page. Remove some images or use auto-arrange.',
                        color: count > 1 ? 'red' : 'orange',
                    });
                }
                break;
            }
            newImages.push({
                id: `${sizeType}-${Date.now()}-${i}`,
                src: activeImageSrc,
                width: scaledW, height: scaledH,
                x: position.x,
                y: position.y,
                isStamp: sizeType === 'stamp',
                sizeType,
                rotation: globalRotation,
                isDual: isActuallyDual,
            });
        }
        if (newImages.length > 0) setImages(prev => [...prev, ...newImages]);
    };

    // ── Quick-add keyboard shortcuts ────────────────────────────────────────
    // Ctrl/Cmd + digit = add that many copies of the first row; Ctrl/Cmd + Shift + digit
    // = STAMPS. 0 counts as 10. preventDefault stops Chrome's tab-switch on the
    // plain-digit combo (interceptable, unlike Ctrl+T/W), and e.code is matched
    // (not e.key) so Shift'ed digit keys ('!', '@'…) still count as digits.
    // Routed through a ref so the keydown listener always calls the latest
    // handleAddImages closure (sizes/settings included) without re-binding.
    const quickAddByKeyRef = useRef(null);
    quickAddByKeyRef.current = (count, stamp) => {
        if (stamp) handleAddImages(stampW, stampH, 'stamp', count);
        else handleAddImages(mainW, mainH, mainType, count);
    };
    // Filled after the action handlers are defined (see above the render return).
    const shortcutActionsRef = useRef({});
    useEffect(() => {
        if (!opened) return undefined;
        const isTyping = (e) => {
            const tag = e.target?.tagName;
            return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || e.target?.isContentEditable;
        };
        const onKeyDown = (e) => {
            // Ctrl/Cmd+P prints THIS sheet instead of the browser's page print.
            if ((e.metaKey || e.ctrlKey) && e.code === 'KeyP') {
                e.preventDefault();
                shortcutActionsRef.current.print?.();
                return;
            }
            // Ctrl/Cmd + digit → passport quick-add; adding Shift → stamp.
            if ((e.metaKey || e.ctrlKey) && !e.altKey && !e.repeat) {
                const m = /^(?:Digit|Numpad)([0-9])$/.exec(e.code || '');
                if (m && !isTyping(e)) {
                    e.preventDefault();
                    const digit = parseInt(m[1], 10);
                    quickAddByKeyRef.current?.(digit === 0 ? 10 : digit, e.shiftKey);
                    return;
                }
            }
            if (e.metaKey || e.ctrlKey || e.altKey || e.repeat) return;
            if (isTyping(e)) return;
            // Delete / Backspace removes the SELECTED images only.
            if (e.code === 'Delete' || e.code === 'Backspace') {
                e.preventDefault();
                shortcutActionsRef.current.removeSelected?.();
                return;
            }
            // Shift+D downloads the sheet as a PDF.
            if (e.code === 'KeyD' && e.shiftKey) {
                e.preventDefault();
                shortcutActionsRef.current.downloadPdf?.();
                return;
            }
        };
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [opened]);

    // A photo's bytes, same-origin (photos are served without CORS headers): the module's own download endpoint by id
    // (the backend proxy for AI / manual / studio / template photos, Bulk Photo Edit's for `bulk:` ids), else a plain fetch.
    const photoBlobFor = async (src) => {
        const match = knownBySrcRef.current.get(src) || allImages.find(a => a.src === src);
        const proxyId = (match?.id && match.id !== 'current') ? match.id
            : ((src === imageUrl || src === activeImageSrc) && imageId && imageId !== 'current' ? imageId : null);
        const bulkId = BULK_ID.exec(String(proxyId || ''))?.[1];
        if (bulkId) return bulkPhotoService.downloadImage(bulkId, 'enhanced');
        if (proxyId) return imageGenerationService.downloadImage(proxyId);
        return (await fetch(src)).blob();
    };

    // Serial cards (shopCard.js): one per different photo on the sheet (the slider's photo when the sheet has none), each
    // with that photo and its file name as the serial, around the shop's name, logo, address and phone. A photo that
    // already has a card is skipped. They join the sheet like any other item (drag, rotate, corner-resize, auto-arrange).
    const shop = selectedOfficeSpace?.businessProfile || {};
    const shopName = shop.shopName || selectedOfficeSpace?.name || '';
    const [addingCard, setAddingCard] = useState(false);
    const handleAddShopCard = async () => {
        if (addingCard) return;
        const sheetPhotos = [...new Set(images.filter((im) => im.sizeType !== 'card' && im.sizeType !== 'qr' && im.src).map((im) => im.src))];
        const carded = new Set(images.filter((im) => im.sizeType === 'card').map((im) => im.forSrc));
        const todo = (sheetPhotos.length ? sheetPhotos : [activeImageSrc].filter(Boolean)).filter((src) => !carded.has(src));
        if (!todo.length) {
            notifications.show({ message: t('imageGeneration.shopCard_allDone'), color: 'blue' });
            return;
        }
        setAddingCard(true);
        try {
            const logo = shop.shopLogo ? await questionPaperService.proxyImageDataUrl(shop.shopLogo).catch(() => null) : null;
            const date = isEn ? new Date().toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' }) : getBnToday();
            const labels = { phone: t('imageGeneration.shopCard_phone'), date: t('imageGeneration.shopCard_date') };
            const shopInfo = { name: shopName, address: shop.businessAddress || '', phone: shop.shopPhone || selectedOfficeSpace?.owner?.phone || '', logo };
            const placed = [...images];
            const added = [];
            let unnamed = 0;
            for (const src of todo) {
                const position = findAvailablePosition(SHOP_CARD_MM.w, SHOP_CARD_MM.h, 'card', placed);
                if (!position) {
                    notifications.show({
                        title: t('imageGeneration.pageFull') || 'Page Full',
                        message: t('imageGeneration.pageFullMessage') || 'No more space available on the page. Remove some images or use auto-arrange.',
                        color: 'orange',
                    });
                    break;
                }
                const entry = knownBySrcRef.current.get(src) || allImages.find((a) => a.src === src);
                const name = sanitizeText(entry?.name || '', { maxLen: 80 }).trim()
                    || `${t('imageGeneration.image') || 'ছবি'} ${bnum(++unnamed)}`;
                const photo = await photoBlobFor(src).then(blobToDataUrl).catch(() => null);
                const card = {
                    id: `card-${Date.now()}-${added.length}`,
                    src: await buildShopCard({ shop: shopInfo, photo, serial: name, date, labels }),
                    forSrc: src,
                    width: SHOP_CARD_MM.w, height: SHOP_CARD_MM.h,
                    x: position.x, y: position.y,
                    isStamp: false,
                    sizeType: 'card',
                    rotation: 0,
                    isDual: false,
                };
                placed.push(card);
                added.push(card);
            }
            if (added.length) setImages((prev) => [...prev, ...added]);
        } catch (err) {
            console.error('Serial card failed:', err);
            notifications.show({ message: t('imageGeneration.shopCard_failed'), color: 'red' });
        } finally {
            setAddingCard(false);
        }
    };

    const handleRemoveImage = (id) => {
        if (selectedIds.has(id) && selectedIds.size > 1) {
            setImages(prev => arrangeList(prev.filter(img => !selectedIds.has(img.id))));
            setSelectedIds(new Set());
            return;
        }
        setImages(prev => arrangeList(prev.filter(img => img.id !== id)));
        setSelectedIds(prev => {
            if (!prev.has(id)) return prev;
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    // handle remove all images
    const handleRemoveAllImages = () => {
        setImages([]);
        setSelectedIds(new Set());
    }

    // Rotate a single image by 90° clockwise — or every selected image when the
    // clicked one is part of the active multi-selection.
    // After rotating an image its bounding box can swap from portrait to landscape
    // (or vice-versa) and hit any neighbour it used to fit next to. Push those
    // neighbours away in the direction of least movement, cascading through any
    // further collisions. Returns the adjusted array, or null if we couldn't
    // find space on the paper (caller falls back to a global re-pack).
    const handleRotateImage = (id) => {
        const bulk = selectedIds.has(id) && selectedIds.size > 1;
        setImages(prev => {
            const rotatedIds = bulk ? Array.from(selectedIds) : [id];
            const rotated = prev.map(img => {
                if (!rotatedIds.includes(img.id)) return img;
                return { ...img, rotation: ((img.rotation || 0) + 90) % 360 };
            });
            return arrangeList(rotated);
        });
    };

    // Current on-screen mm-per-pixel factor (accounts for the CSS paper scale) —
    // shared by drag-end maths and the corner-resize handles.
    const getMmPerPixel = () => {
        if (printRef.current) {
            const rect = printRef.current.getBoundingClientRect();
            return pageH / rect.height;
        }
        return 0.265 / paperScale;
    };

    // Snapshot of every image's box taken when a corner-drag starts. A multi-select
    // group resize scales each follower from its ORIGINAL box (patches stream in as
    // absolute values from the gesture start, so deriving from current state would
    // compound the scale on every pointermove).
    const resizeBaseRef = useRef(null);
    const handleResizeStart = () => {
        resizeBaseRef.current = new Map(images.map(im => [im.id, { w: im.width, h: im.height, x: im.x, y: im.y }]));
    };

    // Corner-resize (ফ্রি সাইজ) — live-updates the dragged image's box, clamped to
    // the sheet. When the dragged image is part of an active multi-selection, every
    // selected image is scaled by the same factors, each anchored at its own
    // top-left corner (only the leader follows the pointer's x/y).
    const handleResizeImage = (id, patch) => {
        const { width: maxW, height: maxH } = getA4Dimensions();
        const clampBox = ({ width, height, x, y }) => {
            width = Math.max(10, width);
            height = Math.max(10, height);
            x = Math.max(0, Math.min(x, maxW - 10));
            y = Math.max(0, Math.min(y, maxH - 10));
            width = Math.min(width, maxW - x);
            height = Math.min(height, maxH - y);
            return { width, height, x, y };
        };
        const leaderBox = clampBox({ width: patch.width, height: patch.height, x: patch.x, y: patch.y });
        // The serial card keeps its shape when a sheet edge clamps one side: shrink the other side to match.
        if (images.find((im) => im.id === id)?.sizeType === 'card' && patch.width > 0) {
            const ratio = patch.height / patch.width;
            leaderBox.width = Math.min(leaderBox.width, leaderBox.height / ratio);
            leaderBox.height = leaderBox.width * ratio;
        }
        const base = resizeBaseRef.current;
        const leaderBase = base?.get(id);
        const bulk = selectedIds.has(id) && selectedIds.size > 1 && leaderBase?.w > 0 && leaderBase?.h > 0;
        // Scale factors come from the CLAMPED leader box, so when the leader hits a
        // sheet edge or the minimum size the whole group stops growing together.
        const scaleW = bulk ? leaderBox.width / leaderBase.w : 1;
        const scaleH = bulk ? leaderBox.height / leaderBase.h : 1;
        setImages(prev => prev.map(im => {
            if (im.id === id) return { ...im, ...leaderBox };
            if (!bulk || !selectedIds.has(im.id)) return im;
            const b = base.get(im.id);
            if (!b) return im;
            return { ...im, ...clampBox({ width: b.w * scaleW, height: b.h * scaleH, x: b.x, y: b.y }) };
        }));
    };

    const handleDragEnd = (event) => {
        const { active, delta } = event;

        if (!delta) return;

        setImages((items) => {
            const activeImage = items.find(item => item.id === active.id);
            if (!activeImage) return items;

            const { width: maxWidth, height: maxHeight } = getA4Dimensions();

            // Convert pixel delta to mm — read actual rendered paper size to account for CSS scale
            const mmPerPixel = (() => {
                if (printRef.current) {
                    const rect = printRef.current.getBoundingClientRect();
                    return pageH / rect.height;
                }
                return 0.265 / paperScale;
            })();

            // ── Bulk drag ────────────────────────────────────────────────────
            // When the dragged image is part of an active multi-selection, move
            // every selected image by the same raw delta, clamping each one
            // individually to the paper bounds. Snap alignment is disabled in
            // this mode — snapping a group to another image's edge would move
            // the leader to a different position than the followers and break
            // their relative layout.
            const bulk = selectedIds.has(active.id) && selectedIds.size > 1;
            if (bulk) {
                const dxMm = delta.x * mmPerPixel;
                const dyMm = delta.y * mmPerPixel;
                return items.map((item) => {
                    if (!selectedIds.has(item.id)) return item;
                    const d = effectiveDims(item);
                    const minPos = getMinGridPos(item);
                    const nx = Math.max(minPos, Math.min(item.x + dxMm, maxWidth  - d.w - minPos));
                    const ny = Math.max(minPos, Math.min(item.y + dyMm, maxHeight - d.h - minPos));
                    return { ...item, x: nx, y: ny };
                });
            }

            let newX = activeImage.x + delta.x * mmPerPixel;
            let newY = activeImage.y + delta.y * mmPerPixel;

            // Constrain to A4 boundaries (stamps keep a getMinGridPos margin from the edge)
            const activeDims = effectiveDims(activeImage);
            const activeMinPos = getMinGridPos(activeImage);
            newX = Math.max(activeMinPos, Math.min(newX, maxWidth  - activeDims.w - activeMinPos));
            newY = Math.max(activeMinPos, Math.min(newY, maxHeight - activeDims.h - activeMinPos));

            // Create test image at new position
            let testImage = {
                ...activeImage,
                x: newX,
                y: newY,
            };

            // Get other images for snap detection
            const otherImages = items.filter(img => img.id !== active.id);

            // Check for snap positions (5mm threshold)
            const snapResult = findSnapPosition(testImage, otherImages);
            if (snapResult.snapped) {
                testImage = {
                    ...testImage,
                    x: snapResult.x,
                    y: snapResult.y,
                };
            }

            // Update position (allow overlapping - user can manually arrange)
            return items.map((item) => {
                if (item.id === active.id) {
                    return testImage;
                }
                return item;
            });
        });
    };

    // Render the whole sheet DETERMINISTICALLY onto a 300-DPI canvas and return it.
    // Shared by BOTH Print and PDF export: drawing to a canvas ourselves (rather than
    // letting the browser rasterise live DOM) is the only way to get a crisp QR — the
    // browser's live-DOM print path resamples the fine QR module grid and warps it, while
    // photos hide the same resampling. Here every module is drawn as an exact block at
    // 300 DPI, so the QR stays sharp and scannable. Mirrors DraggableImageItem's geometry.
    const buildSheetCanvas = async () => {
        const printablePage = document.getElementById('printable-page');
        if (!printablePage) return null;

        // Wait for every photo/QR to finish loading so none render blank on the canvas.
        const domImgs = Array.from(printablePage.querySelectorAll('img'));
        await Promise.all(domImgs.map(img => img.complete ? Promise.resolve() : new Promise((resolve) => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
        })));

        // Photos are served cross-origin without CORS headers, so inline each unique photo
        // as a same-origin data-URL (via the backend proxy, or a plain fetch fallback) so
        // the canvas can actually draw it. QR items are already data-URLs.
        const uniqueSrcs = [...new Set(images.map(im => im.src).filter(Boolean))];
        const srcToData = new Map();
        await Promise.all(uniqueSrcs.map(async (src) => {
            try {
                const blob = src.startsWith('data:') ? await (await fetch(src)).blob() : await photoBlobFor(src);
                srcToData.set(src, await blobToDataUrl(blob));
            } catch (e) {
                console.error('Could not inline image for sheet render:', e);
            }
        }));

        const imgMap = new Map();
        await Promise.all([...srcToData.entries()].map(async ([src, data]) => {
            try { imgMap.set(src, await loadImageEl(data)); } catch { /* skip */ }
        }));

        const DPI = 300;
        const pxPerMm = DPI / 25.4;
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(pageW * pxPerMm);
        canvas.height = Math.round(pageH * pxPerMm);
        const ctx = canvas.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        for (const image of images) {
            const rot = ((image.rotation || 0) % 360 + 360) % 360;
            const isTransposed = rot === 90 || rot === 270;
            const renderedW = isTransposed ? image.height : image.width;
            const renderedH = isTransposed ? image.width : image.height;
            const img = imgMap.get(image.src);

            // ── Shop card: one finished image filling its box (rotatable) ──
            if (image.sizeType === 'card') {
                if (img) {
                    ctx.save();
                    ctx.translate((image.x + renderedW / 2) * pxPerMm, (image.y + renderedH / 2) * pxPerMm);
                    if (rot) ctx.rotate((rot * Math.PI) / 180);
                    ctx.drawImage(img, (-image.width / 2) * pxPerMm, (-image.height / 2) * pxPerMm, image.width * pxPerMm, image.height * pxPerMm);
                    ctx.restore();
                }
                continue;
            }

            // ── QR item: white card, code + shop-name caption (rotatable) ──
            if (image.sizeType === 'qr') {
                const bwPx = image.width * pxPerMm;
                const bhPx = image.height * pxPerMm;
                ctx.save();
                ctx.translate((image.x + renderedW / 2) * pxPerMm, (image.y + renderedH / 2) * pxPerMm);
                if (rot) ctx.rotate((rot * Math.PI) / 180);
                const bx = -bwPx / 2;
                const by = -bhPx / 2;
                ctx.fillStyle = '#ffffff';
                ctx.fillRect(bx, by, bwPx, bhPx);
                const pad = 2 * pxPerMm;
                const capH = image.caption ? 6 * pxPerMm : 0;
                const qrAreaH = Math.max(0, bhPx - 2 * pad - capH);
                const qrAreaW = Math.max(0, bwPx - 2 * pad);
                const qrSize = Math.min(qrAreaW, qrAreaH);
                if (img && qrSize > 0) ctx.drawImage(img, bx + (bwPx - qrSize) / 2, by + pad, qrSize, qrSize);
                if (image.caption) {
                    const capY = by + pad + qrAreaH + 1 * pxPerMm;
                    ctx.strokeStyle = '#e4e4e7';
                    ctx.lineWidth = Math.max(1, 0.3 * pxPerMm);
                    ctx.beginPath();
                    ctx.moveTo(bx + pad, capY);
                    ctx.lineTo(bx + bwPx - pad, capY);
                    ctx.stroke();
                    ctx.fillStyle = '#18181b';
                    ctx.font = `700 ${2.4 * pxPerMm}px 'Noto Sans Bengali','Hind Siliguri',sans-serif`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'top';
                    ctx.fillText(image.caption, bx + bwPx / 2, capY + 1 * pxPerMm, bwPx - 2 * pad);
                }
                ctx.restore();
                continue;
            }

            // ── Photo: replicate the centred frame + cover crop ──
            const isStamp = image.sizeType === 'stamp';
            const uniformInset = isStamp ? imageGap / 4 : imageGap / 2;
            const cAvailW = Math.max(0, renderedW - 2 * uniformInset);
            const cAvailH = Math.max(0, renderedH - 2 * uniformInset);
            const slotAspect = renderedH > 0 ? renderedW / renderedH : 1;
            let frameW = cAvailW;
            let frameH = slotAspect > 0 ? cAvailW / slotAspect : cAvailH;
            if (frameH > cAvailH) { frameH = cAvailH; frameW = cAvailH * slotAspect; }
            const frameLeft = Math.max(0, renderedW - frameW) / 2;
            const frameTop = Math.max(0, renderedH - frameH) / 2;
            const fx = (image.x + frameLeft) * pxPerMm;
            const fy = (image.y + frameTop) * pxPerMm;
            const fwPx = frameW * pxPerMm;
            const fhPx = frameH * pxPerMm;

            if (img) {
                const preW = isTransposed ? fhPx : fwPx;
                const preH = isTransposed ? fwPx : fhPx;
                ctx.save();
                ctx.beginPath();
                ctx.rect(fx, fy, fwPx, fhPx);
                ctx.clip();
                const bgToken = bgBySrc.get(image.src);
                if (bgToken && bgToken !== 'transparent') {
                    if (isSpecialBgToken(bgToken)) {
                        try { paintSpecialBg(ctx, fx, fy, fwPx, fhPx, bgToken); } catch { /* skip preset/image bg */ }
                    } else {
                        const c = resolveSolidBg(bgToken);
                        if (c) { ctx.fillStyle = c; ctx.fillRect(fx, fy, fwPx, fhPx); }
                    }
                }
                ctx.translate(fx + fwPx / 2, fy + fhPx / 2);
                if (rot) ctx.rotate((rot * Math.PI) / 180);
                drawImageCoverPos(ctx, img, -preW / 2, -preH / 2, preW, preH, rot === 0);
                ctx.restore();
            }

            if (showCuttingBorder) {
                ctx.strokeStyle = 'rgb(228,228,231)';
                ctx.lineWidth = Math.max(1, 0.26 * pxPerMm);
                ctx.strokeRect(image.x * pxPerMm, image.y * pxPerMm, renderedW * pxPerMm, renderedH * pxPerMm);
            }
            if (showImageBorder) {
                ctx.strokeStyle = 'rgba(0,0,0,0.6)';
                ctx.lineWidth = Math.max(1, 0.26 * pxPerMm);
                ctx.strokeRect(fx, fy, fwPx, fhPx);
            }
        }

        return canvas;
    };

    const handlePrint = () => {
        if (isPrinting) return;

        // Resolve the ID immediately at click time — before any async work.
        // This guarantees the counter fires even if DOM manipulation or window.print()
        // later throws or behaves unexpectedly on certain browsers/OS.
        const idToTrack = (activeImageId && activeImageId !== 'current') ? activeImageId
                        : (imageId && imageId !== 'current') ? imageId
                        : null;
        if (idToTrack) {
            imageGenerationService.trackPrint(idToTrack)
                .catch(err => console.error('Failed to track print:', err));
        }

        setIsPrinting(true);

        // Print the SAME deterministic 300-DPI canvas the PDF uses — as a single full-page
        // <img> — instead of cloning the live DOM. The old clone path let the browser's print
        // rasteriser resample each element; photos hid it but the QR's fine module grid warped
        // every time. A pre-rendered raster of the whole sheet prints exactly as drawn.
        buildSheetCanvas().then((canvas) => {
            if (!canvas) { setIsPrinting(false); return; }

            // Print from an ISOLATED iframe holding ONLY the sheet image + its own @page
            // rule. Printing from the MAIN document let the app's global @media print / @page
            // rules (e.g. the CV/invoice printers) override the sheet size, so the browser
            // fell back to its default paper (A4) and a 4R/5R/etc. sheet came out the wrong
            // size. A clean iframe document (same pattern as the invoice/booth printers) makes
            // the browser honour the exact selected page size. A BLOB url is used instead of
            // srcdoc because a multi-MB data-URL in srcdoc can print blank in some browsers.
            const imgData = canvas.toDataURL('image/png');
            const docHtml = `<!doctype html><html><head><meta charset="utf-8"><style>
*{margin:0;padding:0;box-sizing:border-box}
html,body{background:#fff}
@page{size:${pageW}mm ${pageH}mm;margin:0}
img{display:block;width:${pageW}mm;height:${pageH}mm}
@media print{html,body{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
</style></head><body><img src="${imgData}"></body></html>`;
            const blob = new Blob([docHtml], { type: 'text/html' });
            const url = URL.createObjectURL(blob);
            const iframe = document.createElement('iframe');
            iframe.setAttribute('aria-hidden', 'true');
            iframe.style.cssText = 'position:fixed;left:-10000px;top:0;width:1px;height:1px;border:0';
            iframe.src = url;
            iframe.onload = () => {
                setTimeout(() => {
                    try { iframe.contentWindow.focus(); iframe.contentWindow.print(); } catch { /* ignore */ }
                    setTimeout(() => {
                        try { document.body.removeChild(iframe); } catch { /* ignore */ }
                        URL.revokeObjectURL(url);
                        setIsPrinting(false);
                    }, 1000);
                }, 200);
            };
            document.body.appendChild(iframe);
        }).catch((err) => {
            console.error('Print render failed:', err);
            setIsPrinting(false);
        });
    };

    // Download the whole sheet as a PDF sized to the selected page size. Renders an
    // unscaled clone of the printable page to a high-res canvas, then places it onto a
    // jsPDF page whose dimensions match the chosen sheet (so 4R exports as a 102×152mm PDF).
    // The chosen download format (remembered in this browser); the button downloads in it, its menu switches and downloads.
    const [dlFormat, setDlFormat] = useState(() => {
        try { const v = localStorage.getItem(DL_FORMAT_KEY); return DOWNLOAD_FORMATS.some((f) => f.key === v) ? v : 'pdf'; } catch { return 'pdf'; }
    });
    const activeFormat = DOWNLOAD_FORMATS.find((f) => f.key === dlFormat) || DOWNLOAD_FORMATS[0];
    const pickFormatAndDownload = (key) => {
        setDlFormat(key);
        try { localStorage.setItem(DL_FORMAT_KEY, key); } catch { /* private mode */ }
        handleDownloadPdf(key);
    };

    const handleDownloadPdf = async (formatKey = dlFormat) => {
        if (isDownloading) return;
        const format = DOWNLOAD_FORMATS.find((f) => f.key === formatKey) || DOWNLOAD_FORMATS[0];
        const printablePage = document.getElementById('printable-page');
        if (!printablePage) return;

        // Count the print as well — same tracking as the Print button.
        const idToTrack = (activeImageId && activeImageId !== 'current') ? activeImageId
                        : (imageId && imageId !== 'current') ? imageId
                        : null;
        if (idToTrack) {
            imageGenerationService.trackPrint(idToTrack)
                .catch(err => console.error('Failed to track print:', err));
        }

        setIsDownloading(true);
        try {
            // Reuse the SAME deterministic 300-DPI sheet renderer as Print, so the PDF and the
            // printed page are pixel-for-pixel identical (crisp QR, correct photos/borders).
            const canvas = await buildSheetCanvas();
            if (!canvas) throw new Error('Sheet canvas render returned null');

            if (format.mime) {
                // JPG / PNG / WebP: the sheet canvas itself, at 300 DPI
                const blob = await new Promise((resolve) => canvas.toBlob(resolve, format.mime, format.quality));
                if (!blob) throw new Error(`${format.key} encode failed`);
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sohozkaj-print-${pageSizeKey}.${format.key}`;
                document.body.appendChild(a);
                a.click();
                a.remove();
                setTimeout(() => URL.revokeObjectURL(url), 2000);
                return;
            }

            // format uses the PORTRAIT base dims + explicit orientation so jsPDF derives
            // the correct page; we then read the real page size back and fill it exactly.
            const pdf = new jsPDF({
                orientation: orientation === 'portrait' ? 'p' : 'l',
                unit: 'mm',
                format: [basePageDims.w, basePageDims.h],
            });
            const pw = pdf.internal.pageSize.getWidth();
            const ph = pdf.internal.pageSize.getHeight();
            pdf.addImage(canvas.toDataURL('image/jpeg', 0.95), 'JPEG', 0, 0, pw, ph);
            pdf.save(`sohozkaj-print-${pageSizeKey}.pdf`);
        } catch (err) {
            console.error('Sheet download failed:', err);
            notifications.show({
                message: format.mime ? t('imageGeneration.sheetDownloadFailed', { format: format.label }) : (t('imageGeneration.pdfDownloadFailed') || 'Failed to generate PDF. Please try again.'),
                color: 'red',
            });
        } finally {
            setIsDownloading(false);
        }
    };

    // Latest action handlers for the keyboard shortcuts (Delete = erase all,
    // D = download PDF, Ctrl/Cmd+P = print) — assigned every render so the
    // keydown listener never calls a stale closure.
    shortcutActionsRef.current = {
        // Delete only removes the CURRENT SELECTION — never wipes the sheet
        // (the erase button stays the explicit clear-all action).
        removeSelected: () => {
            if (selectedIds.size === 0) return;
            setImages(prev => prev.filter(img => !selectedIds.has(img.id)));
            setSelectedIds(new Set());
        },
        // Both no-op on an empty sheet, mirroring the disabled buttons.
        downloadPdf: () => { if (images.length > 0) handleDownloadPdf(); },
        print: () => { if (images.length > 0) handlePrint(); },
    };

    const a4Width = `${pageW}mm`;
    const a4Height = `${pageH}mm`;

    if (!opened) return null;

    return createPortal(
        <div className="fixed inset-0 z-[10000] bg-white dark:bg-zinc-900 flex flex-col"
            style={{ opacity: entered ? 1 : 0, transform: entered ? 'scale(1)' : 'scale(0.985)', transformOrigin: 'center', transition: 'opacity 0.26s ease, transform 0.3s cubic-bezier(0.16, 1, 0.3, 1)' }}>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-200 dark:border-zinc-700 shrink-0">
                <div className="flex items-center gap-2">
                    <img src={logoLight} alt="logo" style={{ height: 28 }} className="dark:hidden" />
                    <img src={logoDark} alt="logo" style={{ height: 28 }} className="hidden dark:block" />
                    <span className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                        {t('imageGeneration.printThisImage') || 'Print This Image'}
                    </span>
                </div>
                <TooltipProvider delayDuration={200}>
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <button
                                onClick={onClose}
                                aria-label={`${t('common.close') || 'Close'} (Esc)`}
                                className="w-7 h-7 rounded bg-red-500 hover:bg-red-600 text-white flex items-center justify-center transition-colors"
                            >
                                <IconX size={14} />
                            </button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="flex items-center gap-1.5">
                            <span>{t('common.close') || 'Close'}</span>
                            <kbd className="px-1.5 py-0.5 rounded bg-zinc-700 dark:bg-zinc-200 text-white dark:text-zinc-900 text-[10px] font-semibold leading-none">
                                Esc
                            </kbd>
                        </TooltipContent>
                    </Tooltip>
                </TooltipProvider>
            </div>

            {/* Body */}
            <div className={styles.printContainer}>
                <div className={`${styles.sidebar} print-sidebar`}>
                    {/* Scrollable content */}
                    <div className={styles.sidebarContent}>
                        <div className="flex flex-col gap-4">

                            {/* Image Slider — 3-image carousel, center = active (larger), infinite loop */}
                            <div>
                                {/* Search — filter old images by name and jump the carousel to a match */}
                                {(
                                    <div className="relative mb-2">
                                        <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                                        <input
                                            type="text"
                                            ref={searchInputRef}
                                            value={imageQuery}
                                            onChange={(e) => setImageQuery(e.target.value)}
                                            maxLength={80}
                                            onFocus={() => setSearchFocused(true)}
                                            // A pick keeps the focus in the box, so a click must reopen the dropdown too.
                                            onClick={() => setSearchFocused(true)}
                                            onBlur={() => setTimeout(() => setSearchFocused(false), 150)}
                                            placeholder={`${t('imageGeneration.searchImages')} · ${t(`imageGeneration.${activeModule.labelKey}`)}`}
                                            className="w-full pl-8 pr-8 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                        />
                                        {/* Empty box: an arrow that opens / closes the module list */}
                                        {!imageQuery && (
                                            <button
                                                type="button"
                                                onMouseDown={(e) => e.preventDefault()}
                                                onClick={() => { searchInputRef.current?.focus(); setSearchFocused((v) => !v); }}
                                                aria-label={t('imageGeneration.printModules')}
                                                aria-expanded={searchFocused}
                                                className="absolute right-1.5 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-md text-zinc-400 hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                                            >
                                                <IconChevronDown size={15} className={`transition-transform ${searchFocused ? 'rotate-180' : ''}`} />
                                            </button>
                                        )}
                                        {imageQuery && (
                                            <Tip label={t('imageGeneration.clearSearch') || 'Clear'}>
                                                <button
                                                    type="button"
                                                    onClick={() => setImageQuery('')}
                                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200"
                                                >
                                                    <IconX size={15} />
                                                </button>
                                            </Tip>
                                        )}

                                        {/* Module dropdown — on focus with nothing typed: which module's images the slider lists */}
                                        {searchFocused && !trimmedQuery && (
                                            <div className="absolute z-20 mt-1 w-full rounded-lg border border-zinc-200 bg-white p-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-800" role="listbox" aria-label={t('imageGeneration.printModules')}>
                                                <p className="px-2 pb-1 pt-0.5 text-[11px] font-semibold text-zinc-400 dark:text-zinc-500">{t('imageGeneration.printModules')}</p>
                                                {PRINT_MODULES.map((m) => {
                                                    const active = m.key === activeModule.key;
                                                    return (
                                                        <button
                                                            key={m.key}
                                                            type="button"
                                                            role="option"
                                                            aria-selected={active}
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => switchModule(m.key)}
                                                            className={`flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs transition-colors ${active ? 'bg-orange-50 font-semibold text-primary dark:bg-orange-900/20' : 'text-zinc-700 hover:bg-zinc-50 dark:text-zinc-200 dark:hover:bg-zinc-700/60'}`}
                                                        >
                                                            <m.Icon size={15} className={active ? 'text-primary' : 'text-zinc-400'} />
                                                            <span className="min-w-0 flex-1 truncate">{t(`imageGeneration.${m.labelKey}`)}</span>
                                                            {active && (moduleLoading ? <IconLoader2 size={14} className="animate-spin" /> : <IconCheck size={14} />)}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {/* Results dropdown */}
                                        {searchFocused && trimmedQuery && (
                                            <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 shadow-lg">
                                                {searchResults.length === 0 ? (
                                                    <div className="px-3 py-3 text-xs text-zinc-500 dark:text-zinc-400 text-center">
                                                        {t('imageGeneration.noImagesFound') || 'No images found'}
                                                    </div>
                                                ) : (
                                                    searchResults.map(({ img, idx }) => (
                                                        <button
                                                            key={img.id}
                                                            type="button"
                                                            onMouseDown={(e) => e.preventDefault()}
                                                            onClick={() => {
                                                                setActiveImageIndex(idx);
                                                                setImageQuery('');
                                                                setSearchFocused(false);
                                                            }}
                                                            className={`w-full flex items-center gap-2 px-2 py-1.5 text-left hover:bg-zinc-50 dark:hover:bg-zinc-700/60 transition-colors ${idx === clampedIndex ? 'bg-orange-50 dark:bg-orange-900/20' : ''}`}
                                                        >
                                                            <img src={img.src} alt="" className="w-8 h-10 object-cover object-top rounded border border-zinc-200 dark:border-zinc-700 flex-shrink-0" style={printBgStyleFor(img.src)} />
                                                            <span className="text-xs text-zinc-700 dark:text-zinc-200 truncate">
                                                                {img.name || `${t('imageGeneration.image') || 'Image'} #${idx + 1}`}
                                                            </span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {allImages.length === 0 && !moduleLoading && (
                                    <p className="py-3 text-center text-xs text-zinc-500 dark:text-zinc-400">{t('imageGeneration.noImagesFound')}</p>
                                )}
                                <div className={styles.imageCarousel}>
                                    {/* Left image — wraps to last when at index 0 */}
                                    {allImages.length > 0 && (() => {
                                        const prevIdx = (clampedIndex - 1 + allImages.length) % allImages.length;
                                        const showPrev = allImages.length > 1;
                                        return (
                                            <div
                                                className={`${styles.carouselSlot} ${styles.carouselSide}`}
                                                onClick={() => showPrev && setActiveImageIndex(prevIdx)}
                                                style={{ visibility: showPrev ? 'visible' : 'hidden' }}
                                            >
                                                {showPrev && (
                                                    <img
                                                        src={allImages[prevIdx].src}
                                                        alt="prev"
                                                        className={styles.carouselImg}
                                                        style={printBgStyleFor(allImages[prevIdx].src)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })()}

                                    {/* Center image (active — larger) */}
                                    <div className={`${styles.carouselSlot} ${styles.carouselCenter}`}>
                                        <img
                                            src={activeImageSrc}
                                            alt="active"
                                            className={styles.carouselImg}
                                            style={printBgStyleFor(activeImageSrc)}
                                            onLoad={(e) => {
                                                const imgEl = e.target;
                                                // Detect dual from the carousel image's own aspect ratio.
                                                // Must NOT fall back to the `isDualImage` prop — that prop
                                                // is frozen to the modal's initial image, so OR'ing with it
                                                // would lock newDual=true forever once opened with a dual
                                                // image, even after the user switches to a single one.
                                                // Dual images are landscape (1251×1024 ≈ 1.22), singles are
                                                // portrait (1024×1251 ≈ 0.82), so anything wider than tall is dual.
                                                const newDual = imgEl.naturalWidth / imgEl.naturalHeight > 1.1;
                                                setIsActuallyDual(newDual);
                                                // Only correct images placed from this specific src that were
                                                // added before the aspect-ratio detection completed.
                                                setImages(prev => prev.map(img => {
                                                    if (img.src !== activeImageSrc || img.isDual === newDual) return img;
                                                    const base = getBaseDims(img.sizeType, newDual);
                                                    if (!base) return img;
                                                    return { ...img, isDual: newDual, width: base.w, height: base.h };
                                                }));
                                            }}
                                        />
                                        <div className={styles.carouselActiveDot} />
                                    </div>

                                    {/* Right image — wraps to first when at last index */}
                                    {allImages.length > 0 && (() => {
                                        const nextIdx = (clampedIndex + 1) % allImages.length;
                                        const showNext = allImages.length > 1;
                                        return (
                                            <div
                                                className={`${styles.carouselSlot} ${styles.carouselSide}`}
                                                onClick={() => showNext && setActiveImageIndex(nextIdx)}
                                                style={{ visibility: showNext ? 'visible' : 'hidden' }}
                                            >
                                                {showNext && (
                                                    <img
                                                        src={allImages[nextIdx].src}
                                                        alt="next"
                                                        className={styles.carouselImg}
                                                        style={printBgStyleFor(allImages[nextIdx].src)}
                                                    />
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>

                                {/* Dot indicators — windowed to max 5 (current ±2) */}
                                {allImages.length > 1 && (() => {
                                    const total = allImages.length;
                                    const maxDots = Math.min(total, 5);
                                    let start = Math.max(0, clampedIndex - 2);
                                    let end = start + maxDots;
                                    if (end > total) { end = total; start = Math.max(0, end - maxDots); }
                                    const visibleIndices = Array.from({ length: end - start }, (_, k) => start + k);
                                    return (
                                        <div className={styles.carouselDots}>
                                            {visibleIndices.map((i) => (
                                                <div
                                                    key={i}
                                                    className={`${styles.dot} ${i === clampedIndex ? styles.dotActive : ''}`}
                                                    onClick={() => setActiveImageIndex(i)}
                                                />
                                            ))}
                                        </div>
                                    );
                                })()}
                            </div>

                            {/* Quick Add */}
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-semibold text-zinc-500 uppercase" style={{ letterSpacing: '0.08em' }}>
                                        {t('imageGeneration.quickAdd') || 'Quick Add'}
                                    </span>
                                    <Tip label="সাইজ সেটিং পরিবর্তন করুন">
                                        <Link
                                            to="/settings?tab=photoeditsettings"
                                            className="text-[10px] underline text-blue-500 hover:text-blue-700 hover:underline leading-none transition-colors"
                                        >
                                            {t('imageGeneration.photoSizeSettings')}
                                        </Link>
                                    </Tip>
                                </div>
                                <div className={styles.quickAddTable}>
                                    {/* Passport row — shows the slider photo's size (visa, e-pass, birth, dual) */}
                                    <div className={styles.quickAddRow}>
                                        <span className={styles.quickAddLabel}>
                                            <span className="text-xs text-zinc-800 dark:text-zinc-100 font-semibold">{mainLabel}</span>
                                            <span className="text-[10px] text-zinc-500">{bnum(`${mainW}×${mainH}`)}mm</span>
                                        </span>
                                        <Tip label={bnum(`${mainLabel} — ${mainW}×${mainH}mm · Ctrl+1`)}><button onClick={() => handleAddImages(mainW, mainH, mainType)} className={`${styles.quickAddBtn} bg-orange-500 hover:bg-orange-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addOne') || '1+'}</button></Tip>
                                        <Tip label={bnum(`${mainLabel} — ${mainW}×${mainH}mm · Ctrl+4`)}><button onClick={() => handleAddImages(mainW, mainH, mainType, 4)} className={`${styles.quickAddBtn} bg-orange-500 hover:bg-orange-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addFour') || '4+'}</button></Tip>
                                        <Tip label={bnum(`${mainLabel} — ${mainW}×${mainH}mm · Ctrl+8`)}><button onClick={() => handleAddImages(mainW, mainH, mainType, 8)} className={`${styles.quickAddBtn} bg-orange-500 hover:bg-orange-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addEight') || '8+'}</button></Tip>
                                    </div>
                                    {/* Stamp row */}
                                    <div className={styles.quickAddRow}>
                                        <span className={styles.quickAddLabel}>
                                            <span className="text-xs text-zinc-800 dark:text-zinc-100 font-semibold">{t('imageGeneration.stamp') || 'Stamp'}</span>
                                            <span className="text-[10px] text-zinc-500">{bnum(`${stampW}×${stampH}`)}mm</span>
                                        </span>
                                        <Tip label={bnum(`স্ট্যাম্প — ${stampW}×${stampH}mm · Ctrl+Shift+1`)}><button onClick={() => handleAddImages(stampW, stampH, 'stamp')} className={`${styles.quickAddBtn} bg-zinc-500 hover:bg-zinc-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addOne') || '1+'}</button></Tip>
                                        <Tip label={bnum(`স্ট্যাম্প — ${stampW}×${stampH}mm · Ctrl+Shift+4`)}><button onClick={() => handleAddImages(stampW, stampH, 'stamp', 4)} className={`${styles.quickAddBtn} bg-zinc-500 hover:bg-zinc-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addFour') || '4+'}</button></Tip>
                                        <Tip label={bnum(`স্ট্যাম্প — ${stampW}×${stampH}mm · Ctrl+Shift+8`)}><button onClick={() => handleAddImages(stampW, stampH, 'stamp', 8)} className={`${styles.quickAddBtn} bg-zinc-500 hover:bg-zinc-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{t('imageGeneration.addEight') || '8+'}</button></Tip>
                                    </div>
                                    {/* 2R / 3R / 4R / 5R / 6R — hidden for dual images (aspect ratio incompatible) */}
                                    {!isActuallyDual && (
                                    <div className={styles.quickAddRow}>
                                        <span className={styles.quickAddLabel}>
                                            <span className="text-xs text-zinc-800 dark:text-zinc-100 font-semibold">R {t('imageGeneration.sizes') || 'Sizes'}</span>
                                            <span className="text-[10px] text-zinc-500">mm</span>
                                        </span>
                                        <Tip label={bnum(`2R — ${size2RW}×${size2RH}mm`)}><button onClick={() => handleAddImages(size2RW, size2RH, '2r')} className={`${styles.quickAddBtn} bg-sky-500 hover:bg-sky-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{bnum('2R')}</button></Tip>
                                        <Tip label={bnum(`3R — ${size3RW}×${size3RH}mm`)}><button onClick={() => handleAddImages(size3RW, size3RH, '3r')} className={`${styles.quickAddBtn} bg-violet-500 hover:bg-violet-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{bnum('3R')}</button></Tip>
                                        <Tip label={bnum(`4R — ${size4RW}×${size4RH}mm${orientation === 'portrait' ? ' · Landscape: 2 fit' : ''}`)}><button onClick={() => handleAddImages(size4RW, size4RH, '4r')} className={`${styles.quickAddBtn} bg-rose-500 hover:bg-rose-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{bnum('4R')}</button></Tip>
                                    </div>
                                    )}
                                    {/* 5R / 6R — second row, aligned under the first three */}
                                    {!isActuallyDual && (
                                    <div className={styles.quickAddRow}>
                                        <span className={styles.quickAddLabel} />
                                        <Tip label={bnum(`5R — ${size5RW}×${size5RH}mm`)}><button onClick={() => handleAddImages(size5RW, size5RH, '5r')} className={`${styles.quickAddBtn} bg-amber-500 hover:bg-amber-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{bnum('5R')}</button></Tip>
                                        <Tip label={bnum(`6R — ${size6RW}×${size6RH}mm`)}><button onClick={() => handleAddImages(size6RW, size6RH, '6r')} className={`${styles.quickAddBtn} bg-teal-500 hover:bg-teal-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{bnum('6R')}</button></Tip>
                                        <Tip label={bnum(`A4 — ${sizeA4W}×${sizeA4H}mm`)}><button onClick={() => handleAddImages(sizeA4W, sizeA4H, 'a4')} className={`${styles.quickAddBtn} bg-emerald-500 hover:bg-emerald-600 text-white text-[16px] font-semibold rounded px-2 py-1 transition-colors`}>{isEn ? 'A4' : 'এ৪'}</button></Tip>
                                    </div>
                                    )}
                                </div>
                            </div>

                            {/* Options group — Free Size / Cutting Border / Image Border toggles
                                bundled into one compact card so the related switches read as a set. */}
                            <div>
                                <span className="text-xs font-semibold text-zinc-500 uppercase mb-1.5 block" style={{ letterSpacing: '0.08em' }}>
                                    {t('imageGeneration.printOptions') || 'Options'}
                                </span>
                                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 overflow-hidden">
                                    {/* Free size — corner-resize handles on every placed image */}
                                    <div className="flex items-center justify-between cursor-pointer select-none px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors" onClick={() => setFreeResize(v => !v)}>
                                        <div className="flex items-center gap-1.5">
                                            <IconArrowsMaximize size={14} className="text-zinc-500" />
                                            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                                                {t('imageGeneration.freeSize') || 'Free Size'}
                                            </span>
                                        </div>
                                        <Checkbox
                                            checked={freeResize}
                                            onCheckedChange={(v) => { setFreeResize(v); }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </div>
                                    {freeResize && (
                                        <p className="text-[10.5px] leading-snug text-zinc-400 px-3 py-1.5 bg-zinc-50 dark:bg-zinc-800/40">
                                            {t('imageGeneration.freeSizeHint') || 'Drag any corner of an image to resize it freely.'}
                                        </p>
                                    )}

                                    {/* Cutting Border Toggle */}
                                    <div className="flex items-center justify-between cursor-pointer select-none px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors" onClick={() => setShowCuttingBorder(v => !v)}>
                                        <div className="flex items-center gap-1.5">
                                            <IconScissors size={14} className="text-zinc-500" />
                                            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                                                {t('imageGeneration.cuttingBorder') || 'Cutting Border'}
                                            </span>
                                        </div>
                                        <Checkbox
                                            checked={showCuttingBorder}
                                            onCheckedChange={(v) => { setShowCuttingBorder(v); }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </div>

                                    {/* Image Border Toggle */}
                                    <div className="flex items-center justify-between cursor-pointer select-none px-3 py-2 hover:bg-zinc-50 dark:hover:bg-zinc-800/60 transition-colors" onClick={() => setShowImageBorder(v => !v)}>
                                        <div className="flex items-center gap-1.5">
                                            <IconBorderOuter size={14} className="text-zinc-500" />
                                            <span className="text-xs font-medium text-zinc-700 dark:text-zinc-200">
                                                {t('imageGeneration.imageBorder') || 'Image Border'}
                                            </span>
                                        </div>
                                        <Checkbox
                                            checked={showImageBorder}
                                            onCheckedChange={(v) => { setShowImageBorder(v); }}
                                            onClick={e => e.stopPropagation()}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Image Gap slider */}
                            <div>
                                <span className="text-xs font-semibold text-zinc-500 uppercase mb-1.5 block" style={{ letterSpacing: '0.08em' }}>
                                    {t('imageGeneration.imageGap') || 'Image Gap'}: <b className="text-zinc-500">{bnum(imageGap)}mm</b>
                                </span>
                                <input
                                    type="range"
                                    className="w-full accent-orange-500 block m-0 align-middle"
                                    value={imageGap}
                                    onChange={e => { gapTouchedRef.current = true; setImageGap(Number(e.target.value)); }}
                                    min={0}
                                    max={10}
                                    step={0.5}
                                />
                                <div className="flex justify-between text-[10px] text-zinc-400 mt-0.5">
                                    <span>{bnum(0)}</span>
                                    <span>{bnum(5)}</span>
                                    <span>{bnum(10)}</span>
                                </div>
                            </div>

                            {/* Page & Layout group — page size, orientation, auto-arrange and
                                zoom bundled together. Inline label+control rows keep it compact. */}
                            <div>
                                <span className="text-xs font-semibold text-zinc-500 uppercase mb-1.5 block" style={{ letterSpacing: '0.08em' }}>
                                    {t('imageGeneration.pageLayoutGroup') || 'Page & Layout'}
                                </span>
                                <div className="rounded-lg border border-zinc-200 dark:border-zinc-700 divide-y divide-zinc-200 dark:divide-zinc-700 overflow-hidden">
                                    {/* Page Size */}
                                    <div className="px-3 py-2">
                                        <div className="flex items-center gap-2">
                                            <span className="text-xs font-medium text-zinc-500 shrink-0 w-16">{t('imageGeneration.pageSize') || 'Page Size'}</span>
                                            <SelectField
                                                value={pageSizeKey}
                                                onChange={setPageSizeKey}
                                                menuZIndex={10001}
                                                options={PAGE_SIZES.map(s => ({
                                                    value: s.key,
                                                    label: s.key === 'custom' ? (t('imageGeneration.customSize') || 'Custom Size') : (isEn ? s.label : bnum(s.label).replace('A৪', 'এ৪').replace('A৫', 'এ৫')),
                                                }))}
                                                triggerClassName="w-full px-2.5 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100"
                                            />
                                        </div>
                                        {pageSizeKey === 'custom' && (
                                            <div className="flex items-center gap-2 mt-2">
                                                <input
                                                    type="number"
                                                    min={20}
                                                    max={1000}
                                                    value={customPageW}
                                                    onChange={e => setCustomPageW(e.target.value)}
                                                    placeholder="W"
                                                    className="w-full px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                                <span className="text-xs text-zinc-400">×</span>
                                                <input
                                                    type="number"
                                                    min={20}
                                                    max={1000}
                                                    value={customPageH}
                                                    onChange={e => setCustomPageH(e.target.value)}
                                                    placeholder="H"
                                                    className="w-full px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 rounded-lg text-xs bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                                <span className="text-xs text-zinc-400 shrink-0">mm</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Page Orientation — full-width toggle (buttons are self-labelling) */}
                                    <div className="px-3 py-2">
                                        <div className="flex w-full rounded-md border border-zinc-300 dark:border-zinc-600 overflow-hidden text-xs">
                                            {['portrait', 'landscape'].map(v => (
                                                <Tip key={v} label={v === 'portrait' ? (t('imageGeneration.portrait') || 'Portrait') : (t('imageGeneration.landscape') || 'Landscape')}>
                                                    <button onClick={() => setOrientation(v)}
                                                        className={`flex-1 py-1 px-2 transition-colors flex items-center justify-center gap-1 ${orientation === v ? 'bg-orange-500 text-white' : 'bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}>
                                                        {v === 'portrait'
                                                            ? <IconRectangleVertical size={12} className="inline" />
                                                            : <IconRectangle size={12} className="inline" />
                                                        }
                                                        {v === 'portrait'
                                                            ? (t('imageGeneration.portrait') || 'Portrait')
                                                            : (t('imageGeneration.landscape') || 'Landscape')
                                                        }
                                                    </button>
                                                </Tip>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Auto Arrange — split button: the main part packs left-aligned
                                        (existing behavior), the chevron opens center/right alignment. */}
                                    <div className="px-3 py-2">
                                        <div className="w-full flex rounded overflow-hidden">
                                            <Tip label={t('imageGeneration.autoArrangeImages') || 'Auto Arrange Images to Fill Blank Space'}>
                                                <button
                                                    onClick={autoArrangeImages}
                                                    className="flex-1 flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 text-white text-sm font-semibold px-3 py-1.5 transition-colors"
                                                >
                                                    <IconLayoutGrid size={15} />
                                                    {t('imageGeneration.autoArrange') || 'Auto Arrange'}
                                                </button>
                                            </Tip>
                                            <DropdownMenu>
                                                <Tip label={t('imageGeneration.arrangeAlignOptions') || 'Alignment options'}>
                                                    <DropdownMenuTrigger asChild>
                                                        <button
                                                            className="flex items-center justify-center px-2 bg-blue-600 hover:bg-blue-700 text-white border-l border-blue-400/60 transition-colors"
                                                        >
                                                            <IconChevronDown size={14} />
                                                        </button>
                                                    </DropdownMenuTrigger>
                                                </Tip>
                                                <DropdownMenuContent align="end" className="z-[10001]">
                                                    <DropdownMenuItem onClick={() => autoArrangeAligned('center')} className="gap-2 text-sm cursor-pointer">
                                                        <IconAlignCenter size={15} className="text-zinc-500" />
                                                        {t('imageGeneration.arrangeCenter') || 'Center align'}
                                                    </DropdownMenuItem>
                                                    <DropdownMenuItem onClick={() => autoArrangeAligned('right')} className="gap-2 text-sm cursor-pointer">
                                                        <IconAlignRight size={15} className="text-zinc-500" />
                                                        {t('imageGeneration.arrangeRight') || 'Right align'}
                                                    </DropdownMenuItem>
                                                </DropdownMenuContent>
                                            </DropdownMenu>
                                        </div>
                                    </div>

                                    {/* Zoom controls — label left, buttons right */}
                                    <div className="px-3 py-2 flex items-center gap-2">
                                        <span className="text-xs font-medium text-zinc-500 shrink-0 w-16">{t('imageGeneration.viewZoom') || 'View Zoom'}</span>
                                        <div className="flex flex-1 items-center gap-1.5">
                                            <Tip label={t('imageGeneration.zoomOut') || 'Zoom out'}>
                                                <button onClick={zoomOut} className="flex-1 h-7 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 flex items-center justify-center transition-colors">
                                                    <IconMinus size={14} />
                                                </button>
                                            </Tip>
                                            <span
                                                className={`flex-1 text-xs font-bold text-center leading-7 cursor-default ${userZoom !== null && userZoom !== 1 ? 'text-violet-600' : 'text-zinc-500'}`}
                                            >
                                                {bnum(Math.round(effectiveScale * 100))}%
                                            </span>
                                            <Tip label={t('imageGeneration.zoomIn') || 'Zoom in'}>
                                                <button onClick={zoomIn} className="flex-1 h-7 rounded border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 hover:bg-zinc-50 dark:hover:bg-zinc-700 text-zinc-700 dark:text-zinc-300 flex items-center justify-center transition-colors">
                                                    <IconPlus size={14} />
                                                </button>
                                            </Tip>
                                            <Tip label={t('imageGeneration.fitToScreen') || 'Fit to screen'}>
                                                <button
                                                    onClick={fitPage}
                                                    className={`flex-1 h-7 rounded flex items-center justify-center transition-colors ${userZoom === 1 || userZoom === null ? 'bg-blue-500 text-white' : 'border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-700'}`}
                                                >
                                                    <IconArrowsMaximize size={14} />
                                                </button>
                                            </Tip>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Serial cards — one per photo on the sheet (shopCard.js) */}
                            <div>
                                <span className="text-xs font-semibold text-zinc-500 uppercase mb-1.5 block" style={{ letterSpacing: '0.08em' }}>
                                    {t('imageGeneration.shopCard_title')}
                                </span>
                                <button
                                    type="button"
                                    onClick={handleAddShopCard}
                                    disabled={addingCard}
                                    className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                    {addingCard ? <IconLoader2 size={17} className="animate-spin" /> : <IconId size={17} />}
                                    {t('imageGeneration.shopCard_add')}
                                </button>
                            </div>

                            {/* Stats */}
                            <div className={`${styles.infoBox} p-2`}>
                                <div className="flex flex-wrap items-center gap-1">
                                    <span className="text-xs text-zinc-500">{t('imageGeneration.totalImages') || 'Total'}:</span>
                                    <span className="text-xs text-zinc-800 dark:text-zinc-100 font-bold">{bnum(images.length)}</span>
                                    {[
                                        { key: 'passport', label: t(isActuallyDual ? 'imageGeneration.abbrDual' : 'imageGeneration.passport'), colorClass: 'text-orange-500' },
                                        { key: 'stamp',    label: t('imageGeneration.stamp') || 'Stamp',       colorClass: 'text-zinc-800 dark:text-zinc-100' },
                                        { key: '2r',       label: bnum('2R'),                                         colorClass: 'text-teal-600' },
                                        { key: '3r',       label: bnum('3R'),                                         colorClass: 'text-teal-600' },
                                        { key: '4r',       label: bnum('4R'),                                         colorClass: 'text-teal-600' },
                                        { key: '5r',       label: bnum('5R'),                                         colorClass: 'text-teal-600' },
                                        { key: '6r',       label: bnum('6R'),                                         colorClass: 'text-teal-600' },
                                        { key: 'a4',       label: isEn ? 'A4' : 'এ৪',                                         colorClass: 'text-teal-600' },
                                        ...PHOTO_PRINT_SIZES.map((s) => {
                                            const { w, h } = photoPrintDims(s, ps);
                                            return { key: s.key, label: bnum(`${t(`imageGeneration.${s.labelKey}`)} ${w}×${h}`), colorClass: 'text-orange-500' };
                                        }),
                                    ].map(({ key, label, colorClass }) => {
                                        const count = images.filter((img) => img.sizeType === key).length;
                                        return count > 0 ? (
                                            <div key={key} className="flex items-center gap-1">
                                                <span className="text-xs text-zinc-500">·</span>
                                                <span className={`text-xs font-bold ${colorClass}`}>{label}: {bnum(count)}</span>
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                            </div>

                        </div>
                    </div>

                    {/* Fixed footer: compact Erase + PDF row, full-width Print below */}
                    <div className={styles.sidebarFooter}>
                        <div className="flex flex-col gap-2">
                            <div className="flex items-stretch gap-2">
                                <Tip label={t('imageGeneration.eraseAllTooltip') || 'Remove every image from the sheet'}>
                                    <button
                                        onClick={handleRemoveAllImages}
                                        aria-label={t('imageGeneration.erase') || 'Erase'}
                                        className="flex-none flex items-center justify-center w-10 bg-red-100 hover:bg-red-200 dark:bg-red-900/30 dark:hover:bg-red-900/50 text-red-600 dark:text-red-400 rounded-lg transition-colors"
                                    >
                                        <IconTrash size={17} />
                                    </button>
                                </Tip>
                                {/* Download — the chosen format; the arrow picks PDF / JPG / PNG / WebP and downloads in it */}
                                <div className="flex flex-1 min-w-0 items-stretch overflow-hidden rounded-lg bg-primary/10 text-primary">
                                    <Tip label={`${t('imageGeneration.downloadSheetTooltip', { format: activeFormat.label })} · Shift+D`}>
                                        <button
                                            onClick={() => handleDownloadPdf()}
                                            disabled={isDownloading || images.length === 0}
                                            className="flex-1 min-w-0 flex items-center justify-center gap-1.5 hover:bg-primary/20 disabled:opacity-60 disabled:cursor-not-allowed text-xs font-semibold px-2 py-2.5 transition-colors"
                                        >
                                            <activeFormat.Icon size={16} className="flex-shrink-0" />
                                            <span className="whitespace-nowrap !text-xs">{isDownloading
                                                ? (t('imageGeneration.generatingPdf') || 'Generating…')
                                                : t('imageGeneration.downloadAs', { format: activeFormat.label })}</span>
                                        </button>
                                    </Tip>
                                    <DropdownMenu>
                                        <Tip label={t('imageGeneration.downloadFormat')}>
                                            <DropdownMenuTrigger asChild>
                                                <button
                                                    disabled={isDownloading || images.length === 0}
                                                    aria-label={t('imageGeneration.downloadFormat')}
                                                    className="flex items-center justify-center px-2 border-l border-primary/20 hover:bg-primary/20 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                                                >
                                                    <IconChevronDown size={14} />
                                                </button>
                                            </DropdownMenuTrigger>
                                        </Tip>
                                        <DropdownMenuContent align="end" side="top" className="z-[10001] min-w-[170px]">
                                            {DOWNLOAD_FORMATS.map((f) => (
                                                <DropdownMenuItem key={f.key} onClick={() => pickFormatAndDownload(f.key)} className="gap-2 text-sm cursor-pointer">
                                                    <f.Icon size={15} className={f.key === dlFormat ? 'text-primary' : 'text-zinc-500'} />
                                                    <span className="flex-1">{t('imageGeneration.downloadAs', { format: f.label })}</span>
                                                    {f.key === dlFormat && <IconCheck size={14} className="text-primary" />}
                                                </DropdownMenuItem>
                                            ))}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>
                            </div>
                            <Tip label={`${t('imageGeneration.printTooltip') || 'Print this sheet'} · Ctrl+P`}>
                                <button
                                    onClick={handlePrint}
                                    disabled={isPrinting || images.length === 0}
                                    className={`${styles.printBtn} w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 disabled:opacity-60 disabled:cursor-not-allowed text-white text-sm font-semibold rounded px-3 py-1.5 transition-colors`}
                                >
                                    <IconPrinter size={20} />
                                    {isPrinting ? '...' : (t('imageGeneration.print') || 'Print')}
                                </button>
                            </Tip>
                        </div>
                    </div>
                </div>

                <div
                    className={styles.previewArea}
                    ref={previewAreaRef}
                    onMouseDown={handlePreviewPanStart}
                    onMouseMove={handlePreviewPanMove}
                    onMouseUp={handlePreviewPanEnd}
                    onMouseLeave={handlePreviewPanEnd}
                    style={{ cursor: isPanning ? 'grabbing' : 'grab' }}
                >
                    {/* Inner centering wrapper — top-aligned on compact screens, centered on desktop */}
                    <div style={{
                        display: 'flex',
                        alignItems: (isCompact || isMobileLayout) ? 'flex-start' : 'center',
                        justifyContent: 'center',
                        minWidth: '100%',
                        minHeight: '100%',
                        paddingTop: (isCompact || isMobileLayout) ? '8px' : 0,
                    }}>
                    <div style={{
                        width: `${pageW * (96 / 25.4) * effectiveScale}px`,
                        height: `${pageH * (96 / 25.4) * effectiveScale}px`,
                        flexShrink: 0,
                        overflow: 'hidden',
                    }}>
                    <div
                        id="printable-page"
                        ref={printRef}
                        className={`${styles.a4Paper} ${orientation === 'landscape' ? 'landscape' : ''}`}
                        style={{
                            width: a4Width,
                            height: a4Height,
                            transformOrigin: 'top left',
                            transform: `scale(${effectiveScale})`,
                            cursor: 'default',
                        }}
                        onMouseDown={handlePaperMouseDown}
                        onMouseMove={handlePaperMouseMove}
                        onMouseUp={handlePaperMouseUp}
                        onMouseLeave={handlePaperMouseUp}
                    >
                        <DndContext
                            sensors={sensors}
                            onDragEnd={handleDragEnd}
                            accessibility={{
                                announcements: {
                                    onDragStart: () => '',
                                    onDragOver: () => '',
                                    onDragEnd: () => '',
                                    onDragCancel: () => '',
                                },
                            }}
                        >
                            <div
                                className={styles.imageGrid}
                                style={{
                                    top:    `${pagePadding}mm`,
                                    left:   `${pagePadding}mm`,
                                    width:  `${pageW - 2 * pagePadding}mm`,
                                    height: `${pageH - 2 * pagePadding}mm`,
                                }}
                            >
                                {images.map((image) => (
                                    <DraggableImageItem
                                        key={image.id}
                                        id={image.id}
                                        image={image}
                                        onRemove={handleRemoveImage}
                                        onRotate={handleRotateImage}
                                        onToggleSelect={toggleSelect}
                                        selected={selectedIds.has(image.id)}
                                        showCuttingBorder={showCuttingBorder}
                                        showImageBorder={showImageBorder}
                                        imageGap={imageGap}
                                        resizable={freeResize}
                                        onResize={handleResizeImage}
                                        onResizeStart={handleResizeStart}
                                        getMmPerPixel={getMmPerPixel}
                                        bgStyle={printBgStyleFor(image.src)}
                                    />
                                ))}
                                {/* Individual selection highlight per selected image — one border
                                    per image so Ctrl+click deselect targets are visually clear. */}
                                {!marquee && selectedIds.size > 0 && images
                                    .filter(img => selectedIds.has(img.id))
                                    .map(img => {
                                        const d = effectiveDims(img);
                                        return (
                                            <div
                                                key={`sel-${img.id}`}
                                                className={styles.selectionBox}
                                                data-print-hide="true"
                                                style={{
                                                    left:   `${img.x}mm`,
                                                    top:    `${img.y}mm`,
                                                    width:  `${d.w}mm`,
                                                    height: `${d.h}mm`,
                                                }}
                                            />
                                        );
                                    })
                                }
                                {/* Marquee rectangle — rendered only while the user is mid-drag. */}
                                {marquee && (
                                    <div
                                        className={styles.marquee}
                                        data-print-hide="true"
                                        style={{
                                            left:   `${marquee.x}mm`,
                                            top:    `${marquee.y}mm`,
                                            width:  `${marquee.w}mm`,
                                            height: `${marquee.h}mm`,
                                        }}
                                    />
                                )}
                            </div>
                        </DndContext>
                    </div>
                    </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
}
